/*
  assets/infinite-article.js
  Shopify 記事無限スクロール loader（要件定義書 v0.9 準拠 / v1 スコープ）

  対応要件（主なもの）
    U0  : 着地URLの allowlist 判定・fail-closed
    U1  : 生成するURLは常にクリーンな絶対URL
    U2  : transport は clean_url + "?view=infinite" のみ
    U3  : n / 入口ID / infinite 判定は URL に載せず dataLayer で運ぶ
    S4  : 可視 next リンクは preventDefault せず fetch トリガーにもしない
    S7  : canonical/robots/OG/JSON-LD は触らない。document.title のみ追随
    S8  : payload 以外（head 断片・robots meta・JSON-LD・script）を親DOMへ入れない
    S9  : 追記記事のタイトルは h2（transport 側で出力済み）
    S10 : isTrusted な実操作の latch 後にのみ IntersectionObserver を有効化
    S11 : 追記上限（記事数 / バイト数 / DOMノード数）
    S12 : 同一チェーンで同じ記事を2回 append しない
    S14 : 上方向スクロールでも URL/title を復元（中央バンド判定でヒステリシス）
    F1  : payload 外を import・実行しない
    F3  : append フェーズの状態機械（in-flight 1件・ステータス別方針・abort）
    F3b : primary-visible フェーズ（replaceState 成功後に title と PV を commit）
    F4  : 契約検証（schema / generation / discriminator / article_id / clean_url / payload sentinel）
    F5  : same-origin / redirect:error / credentials / cache:no-store / MIME essence / URL 検証
    F6  : 1 document 1 article 1 PV。dataLayer 経由。初期Aは自動計測に任せる
    F13 : ID / IDREF の名前空間化（本文の list1 / cta-0001 衝突対策）
    F14 : URL 参照の凍結（相対URLを記事B基準で絶対化・base 除去・本文アンカーは URL を変えない）
    F16 : feature generation の検証と REVOKED
*/
(function () {
  'use strict';

  // デバッグは次のいずれかで有効化する:
  //   window.INFINITE_ARTICLE_DEBUG = true   （スクリプト読み込み前に設定した場合のみ有効）
  //   localStorage.setItem('INFINITE_ARTICLE_DEBUG', '1')  → リロードで有効（推奨）
  var DEBUG = false;
  try {
    DEBUG = !!window.INFINITE_ARTICLE_DEBUG || localStorage.getItem('INFINITE_ARTICLE_DEBUG') === '1';
  } catch (e) { DEBUG = !!window.INFINITE_ARTICLE_DEBUG; }
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[infinite]'].concat([].slice.call(arguments)));
  }

  // 制御文字判定（U0）。正規表現のエスケープを使わず charCode で判定する。
  function hasControlChar(str) {
    for (var i = 0; i < str.length; i++) {
      var c = str.charCodeAt(i);
      if (c < 32 || c === 127) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * 設定（実装時に確定する数値。要件 S11 / F3 / 4-4）
   * ------------------------------------------------------------------ */
  var CFG = {
    viewParam: 'view',
    viewValue: 'infinite',
    fetchTimeoutMs: 12000,        // 1試行のタイムアウト
    totalRetryBudgetMs: 30000,    // リトライ総時間
    maxRetriesPerCandidate: 2,    // 5xx / offline / network error のみ
    maxHops: 120,                 // traversal 上限（append 上限とは別）。追記40本＋拒否ぶんの余裕
    maxBytesPerResponse: 700 * 1024,
    maxBytesCumulative: 8000 * 1024,
    prefetchListTags: 6,          // latch 直後に先読みするタグ数（一覧1ページ目）
    maxListFetches: 48,           // 一覧の実通信回数の上限（グループ×帯でソースが増えるため）
    maxListBytes: 300 * 1024,     // 一覧 transport は本文を含まないので小さい上限で十分
    // 一覧に使ってよい累計バイト（本文とは別枠）。実測の一覧応答は約20KBなので
    // 2,000KB あれば 100ページ弱を掘れる。残り 6,000KB 以上は必ず本文に残る。
    maxListBytesCumulative: 2000 * 1024,
    listTimeoutMs: 8000,
    maxNodesCumulative: 40000,
    // CLS 対策（Codex 1巡目 §8）: 挿入点を画面外に保つための先読み距離と、
    // 追記前に画像の自然寸法を解決するための上限時間。
    appendPreloadPx: 1200,
    // タグ候補の選び方。最新5本を決定的に取ると、そのタグの記事すべてが
    // 同じ5本を見てしまう（実測 98.7%）。最新に寄せた重み付きランダム抽出にする。
    //   recencyExponent : 大きいほど新着に寄る。0 = 完全にランダム
    //   deepPageChance  : 2ページ目以降を覗く確率（カタログ網羅のため）
    //   pageBiasExp     : ページ選択の前方バイアス。大きいほど若いページに寄る
    recencyExponent: 1.0,
    deepPageChance: 0.25,
    // 'fixed' = 公開日が新しい順に固定（誰が読んでも同じ並び）
    // 'weighted' = 新着重み付きの抽選（読む人ごとに変わる。従来の挙動）
    // fixed のときは深いページの抽選も行わない（順番が飛ぶため）。
    orderMode: 'fixed',
    pageBiasExp: 2,
    listPerPage: 50,               // list transport の per_page と一致させること
    // 本文に含まれる <style> を許可するか（テーマ設定）。許可しても script 等は拒否する。
    allowStyle: false,
    // 'link' = 記事ページへのリンク（既定）/ 'expand' = その場で全文展開（従来）
    excerptAction: 'link',
    excerptMode: 'headings',      // 'off' / 'headings'（見出し数で切る）/ 'height'（高さで切る）
    excerptHeadings: 2,           // headings のとき、本文の先頭から残す H2〜H6 の数
    excerptHeight: 2000,          // height のとき、残す高さ(px)
    excerptLabel: '続きを読む',
    scriptPolicy: 'strip',
    scriptExecHandles: [],        // このパターンを handle に含む記事だけ、安全なスクリプトを実行する
    hideSelectors: [],            // 追記記事から取り除く要素（動かない UI）        // 'strip'（本文の script を除去して表示）/ 'reject'（記事ごと捨てる）
    imgDimTimeoutMs: 400,
    insertDeferMaxMs: 2500,
    // 発火タイミング（テーマ設定「追記のタイミング」で上書きされる）
    sentinelRootMargin: '0px 0px',     // 先読み距離。広いと短い記事で連鎖読み込みになる
    triggerMargin: 0,                  // スクロール位置判定でも同じ距離を使う
    minScrollSinceAppend: 700,         // 1本追記したあと、次を読むまでに必要なスクロール量
    consumeRatio: 1.0,                 // 直前記事の読み終わり判定（1.0 = 末尾が見えるまで待つ）
    primaryLineRatio: 0.25,            // 読み進め線の位置（画面の高さに対する比）
    primaryBackMargin: 150,            // 上方向へ戻すときだけ効かせる不感帯（px）。0 で無効。
                                       // 判定は checkPrimaryByRect の「線」1本で行うため、
                                       // 境目でスクロールが上下に揺れると URL が往復する。
                                       // measurement_mode='auto' では replaceState のたびに
                                       // GA4 の履歴PVが飛ぶ（記事ごとの重複防止 pvState は
                                       // manual のときしか通らない）ので、PV が水増しされる。
                                       // 下方向は体感に合わせて線ちょうどのまま切り替える。
    primaryRootMargin: '-20% 0px -70% 0px', // 読み進め線をまたぐ 10% バンド = 主可視。
                                       // IO は「再評価のきっかけ」でしかなく、判定そのものは
                                       // 常に checkPrimaryByRect の線で行う（= このバンドは
                                       // ヒステリシスにならない。不感帯は primaryBackMargin）。
                                       // primaryLineRatio から applyTriggerProfile で組み立て直す。
    idPrefix: 'ia',
    // U0: 許可する tracking パラメータ（exact name。prefix ワイルドカードは使わない）
    allowedQuery: [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
      'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', '_gl',
      'fbclid', 'yclid', 'msclkid',
      // 本機能が表示URLに付ける計測用パラメータ（テーマ設定で ON のときのみ付与される）。
      // 共有・リロードされても loader が止まらないよう allowlist に含める。
      'n', 'e'
    ],
    // linker が消費する可能性のあるパラメータ（存在時は History 更新を止める / F6 linker ゲート）
    linkerQuery: ['gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', '_gl'],
    maxParamLength: 512
  };

  /* ------------------------------------------------------------------ *
   * bootstrap 契約の読み込み（F4）
   * ------------------------------------------------------------------ */
  var bootstrapEl = document.querySelectorAll('[data-infinite-contract-kind="bootstrap"]');
  if (bootstrapEl.length !== 1) { log('bootstrap contract not found or duplicated'); return; }

  var boot;
  try {
    boot = JSON.parse(bootstrapEl[0].getAttribute('data-infinite-contract'));
  } catch (e) { log('bootstrap parse failed', e); return; }

  // v2 で list 契約に handle / rank が必須になった。旧 v1 契約と新 JS が混在すると
  // 候補全滅（handle 欠落の fail closed）や誤った重み（rank 推定）になるため、
  // 静かに動かさず機能を開始しない。
  if (!boot || boot.contract_schema_version !== '2') { log('schema mismatch'); return; }
  if (boot.feature_enabled !== true) { log('feature disabled'); return; }
  if (!boot.initial_article || boot.initial_article.eligibility !== true) { log('initial ineligible'); return; }

  var container = document.getElementById('infinite-article-container');
  if (!container) { log('container missing'); return; }

  // 追記のタイミング。lazy ほど「読み終わってから」次を読み込む。
  (function applyTriggerProfile() {
    // preload = 記事の終端から何px手前で次の取得を始めるか。
    // 実測（media/right-side・20本）: 記事の高さは平均 8,784px・最大 16,181px、
    // transport の取得は 22〜46KB / 平均 248ms（PC・有線）。
    // 旧設定の 1,200px は、フリックスクロール（3,000〜8,000px/s）だと 150〜400ms で
    // 通過してしまい、取得が終わる前に読者が最下部へ着く。
    // → 「読み込まれない」「少し待つと出てくる」の正体。
    // consume は preload より **先に** 開くようにする（そうしないと consume が律速になる）。
    //   consume 0.7 → 残り 30% = 平均 2,635px、preload 2,500px とほぼ同じ位置で開く。
    var profiles = {
      eager:  { margin: 400, minScroll: 200, consume: 0.4, preload: 5000 },
      normal: { margin: 150, minScroll: 450, consume: 0.6, preload: 3500 },
      lazy:   { margin: 0,   minScroll: 500, consume: 0.7, preload: 2500 }
    };
    var pf = profiles[boot.trigger_profile] || profiles.lazy;
    CFG.triggerMargin = pf.margin;
    CFG.appendPreloadPx = pf.preload;
    // 下端 = プロファイルの margin + 先読み距離。rootMargin は上・右・下・左の順。
    // scroll フォールバック側（vh + triggerMargin + appendPreloadPx）と同じ境界にそろえる。
    // 追記の「間隔」は readyForNextAppend()（minScroll / consume）で決まるので、
    // ここを広げても追記が早まることはない。挿入点を画面外に保つのが目的。
    CFG.sentinelRootMargin = '0px 0px ' + (pf.margin + CFG.appendPreloadPx) + 'px 0px';
    // 新着への寄せ度はテーマ設定で調整できる（契約経由）。範囲外の値は既定に戻す。
    CFG.allowStyle = boot.allow_style === true;
    if (boot.script_policy === 'reject' || boot.script_policy === 'strip') CFG.scriptPolicy = boot.script_policy;
    CFG.scriptExecHandles = splitSetting(boot.script_exec_handles, true);
    CFG.hideSelectors = splitSetting(boot.hide_selectors, false);   // CSSセレクタなので小文字化しない
    if (boot.excerpt_mode === 'off' || boot.excerpt_mode === 'headings' || boot.excerpt_mode === 'height') {
      CFG.excerptMode = boot.excerpt_mode;
    }
    var eh = parseInt(boot.excerpt_headings, 10);
    if (isFinite(eh) && eh >= 1 && eh <= 10) CFG.excerptHeadings = eh;
    if (boot.excerpt_action === 'link' || boot.excerpt_action === 'expand') {
      CFG.excerptAction = boot.excerpt_action;
    }
    var ehp = parseInt(boot.excerpt_height, 10);
    if (isFinite(ehp) && ehp >= 400 && ehp <= 8000) CFG.excerptHeight = ehp;
    if (typeof boot.excerpt_label === 'string' && boot.excerpt_label) CFG.excerptLabel = boot.excerpt_label;
    var rx = parseFloat(boot.recency_exponent);
    // 上限はテーマ設定 infinite_article_recency の最大値（2.5）と一致させる。
    // 契約は信頼済みだが、UI に無い値が来たら既定へ戻す。
    if (isFinite(rx) && rx >= 0 && rx <= 2.5) CFG.recencyExponent = rx;
    if (boot.order_mode === 'fixed' || boot.order_mode === 'weighted') {
      CFG.orderMode = boot.order_mode;
    }
    CFG.minScrollSinceAppend = pf.minScroll;
    CFG.consumeRatio = pf.consume;

    // 読み進め線。契約に無い（旧テーマ）／範囲外なら既定のまま。
    var pl = parseFloat(boot.primary_line_ratio);
    if (isFinite(pl) && pl >= 0.1 && pl <= 0.5) CFG.primaryLineRatio = pl;
    // IO のバンドを読み進め線にそろえる（ずれていても rect 判定で結論は同じだが、
    // 発火の位置が合わないと無駄な再判定が増える）。
    var above = Math.round(CFG.primaryLineRatio * 100) - 5;
    var below = Math.round((1 - CFG.primaryLineRatio) * 100) - 5;
    if (above >= 0 && below >= 0) {
      CFG.primaryRootMargin = '-' + above + '% 0px -' + below + '% 0px';
    }
  })();

  /* ------------------------------------------------------------------ *
   * U0: 着地URLの判定（allowlist / fail-closed）
   * ------------------------------------------------------------------ */
  var landing = new URL(window.location.href);
  var hasLinkerParam = false;

  for (var it = landing.searchParams.keys(), k = it.next(); !k.done; k = it.next()) {
    var name = k.value;
    if (CFG.allowedQuery.indexOf(name) === -1) { log('U0 fail-closed: unexpected query', name); return; }
    var vals = landing.searchParams.getAll(name);
    if (vals.length > 1) { log('U0 fail-closed: duplicated query', name); return; }
    var v = vals[0] || '';
    if (v.length > CFG.maxParamLength) { log('U0 fail-closed: param too long', name); return; }
    if (hasControlChar(v)) { log('U0 fail-closed: control char', name); return; }
    if (CFG.linkerQuery.indexOf(name) !== -1) hasLinkerParam = true;
  }

  // fragment: 通常のページ内アンカーと scroll-to-text だけ許可し、それ以外は fail-closed
  // （Conversion Linker が fragment に _gl を置く構成があるため）
  var hash = landing.hash || '';
  if (hash) {
    // URL.hash は percent-encoded のまま返るため、そのまま ASCII 判定すると
    // 日本語や空白を含む正当なアンカー（#目次 など）で機能全体が止まる。必ず decode してから判定する。
    var rawFrag = hash.slice(1);
    var decodedFrag = rawFrag;
    try { decodedFrag = decodeURIComponent(rawFrag); } catch (e) { decodedFrag = rawFrag; }

    if (/_gl|gclid|gclsrc|dclid|gbraid|wbraid/i.test(decodedFrag)) { log('U0 fail-closed: linker in fragment'); return; }

    var scrollToText = rawFrag.indexOf(':~:text=') === 0;
    if (scrollToText) {
      if (rawFrag.length > 1024) { log('U0 fail-closed: scroll-to-text が長すぎる'); return; }
    } else {
      if (decodedFrag.length > 200 || hasControlChar(decodedFrag)) { log('U0 fail-closed: fragment', hash); return; }
      // 実在するアンカーなら許可する（ページ内リンク経由の着地）
      var fragTargetOk = false;
      try {
        fragTargetOk = !!document.getElementById(decodedFrag) ||
                       !!document.querySelector('[name="' + decodedFrag.replace(/["\\]/g, '\\$&') + '"]');
      } catch (e) { fragTargetOk = false; }
      if (!fragTargetOk) { log('U0 fail-closed: 不明な fragment', decodedFrag); return; }
    }
  }

  // F6 linker ゲート: click ID が消費された保証が無いので History 更新はしない（append と計測は続ける）
  var historyEnabled = !hasLinkerParam;
  if (!historyEnabled) log('history updates disabled (linker params present)');

  /* ------------------------------------------------------------------ *
   * 状態（F3 / F6 / F16）
   * ------------------------------------------------------------------ */
  var ST = { UNSEEN: 0, FETCHING: 1, RETRYABLE: 2, TERMINAL: 3, VALIDATED: 4, APPENDED: 5, INELIGIBLE: 6 };
  var PV = { NOT_ATTEMPTED: 0, RESERVED_FOR_AUTO: 1, ENQUEUED: 2 };

  var state = {
    generation: boot.feature_generation,
    revoked: false,
    latched: false,
    inFlight: null,          // document 全体で同時1件（F16 が依存）
    hops: 0,
    appended: 0,
    bytes: 0,
    nodes: 0,
    maxAppends: Math.max(1, Math.min(40, parseInt(boot.max_appends, 10) || 3)),
    candidateState: Object.create(null),   // articleId -> ST
    pvState: Object.create(null),          // articleId -> PV
    seq: 0,                                // U3: content_sequence_index（append 順）
    measurementMode: (boot.measurement_mode === 'manual' || boot.measurement_mode === 'off') ? boot.measurement_mode : 'auto',
    entryArticleId: String(boot.initial_article.article_id),
    // 表示URLの e= に使う。数値IDだとレポートを見たときにどの記事か分からないため handle にする。
    // 旧契約（handle 無し）ではIDへフォールバックする。
    entryArticleHandle: (typeof boot.initial_article.handle === 'string' && boot.initial_article.handle)
      ? boot.initial_article.handle
      : String(boot.initial_article.article_id),
    articles: [],                          // { id, cleanUrl, title, el }
    current: null,
    next: boot.next_candidate,
    // 入口記事のリスト指定（custom.infinite_next_list）。存在する場合はこれがチェーン全体を支配し、
    // 各記事の next は参照しない（= 次を再計算しないのでループしない）
    chain: (boot.chain && boot.chain.length) ? boot.chain.slice() : [],
    chainIdx: 0,
    urlParams: boot.url_params_enabled === true,   // Forbes 型 ?n=&e= を付けるか
    aborter: null
  };

  var initialId = String(boot.initial_article.article_id);
  state.candidateState[initialId] = ST.APPENDED;
  // 初期Aは GA4 の自動ページビューで計測される前提（案B-1）。manual では絶対に送らない。
  state.pvState[initialId] = PV.RESERVED_FOR_AUTO;

  // 初期記事の可視判定対象（Dawn の記事本体）。
  // 以前は main へフォールバックしていたが、main は追記後も画面全体を覆い続けるため、
  // 主可視判定が初期記事から動かなくなり URL も title も切り替わらない。要素が無ければ起動しない。
  var initialEl = document.querySelector('article.article-template');
  if (!initialEl) { log('article.article-template が見つからないため起動しません'); return; }
  state.articles.push({
    id: initialId,
    cleanUrl: boot.initial_article.clean_url,
    // 契約の page_title ではなく実際の <title> を保存する。
    // テーマが「タイトル – ショップ名」のように装飾していると、戻ったときだけ表記が変わるため。
    title: document.title || boot.initial_article.document_title,
    el: initialEl
  });
  state.current = state.articles[0];

  /* ------------------------------------------------------------------ *
   * 元記事のコメント欄を、追記記事より後ろへ移す
   * ------------------------------------------------------------------ *
   * テーマはコメント欄を記事本文の直後に置く。無限スクロールのコンテナは
   * その更に下になるため、コメント投稿フォームが「ページの終わり」に見えて
   * 下に記事が続くことに気づかれない（実測: prna テンプレートで顕著）。
   * サーバ出力は変えず、DOM 上の位置だけ入れ替える（JS 無効なら元のまま / F8）。
   * ------------------------------------------------------------------ */
  // 末尾へ移したコメントフォーム。移した場合だけ保持する。
  // 元の位置（入口記事の直後）にあるままなら、投稿先も入口記事のままが正しい。
  var movedCommentForm = null;

  (function handleComments() {
    // 契約に無い（設定 keep / 旧テーマ）なら何もしない
    var sel = boot.comment_selector;
    var mode = boot.comment_mode;
    if (typeof sel !== 'string' || !sel || (mode !== 'hide' && mode !== 'move')) return;
    // コメント投稿直後は Shopify が #comments へ戻す。動かすと着地点がずれるので触らない。
    if (window.location.hash === '#comments' ||
        window.location.search.indexOf('comment_posted') !== -1) {
      log('コメント投稿直後のため、コメント欄はそのままにします');
      return;
    }
    var cm;
    try { cm = document.querySelector(sel); } catch (e) { log('comment_selector が不正:', sel); return; }
    if (!cm || cm.parentNode === null) return;

    if (mode === 'hide') {
      // 投稿フォームだけ消す。既に付いているコメントは記事の内容なので残す
      // （実測: 26,694 記事のうちコメントがあるのは 42 記事 / 合計 103 件）。
      var forms = cm.getElementsByTagName('form');
      var n = forms.length;
      for (var fi = n - 1; fi >= 0; fi--) {
        if (forms[fi].parentNode) forms[fi].parentNode.removeChild(forms[fi]);
      }
      // 残りがコメント一覧でもなければ、枠ごと消す（見出しだけが残らないように）
      if (!cm.querySelector('.article-template__comments')) {
        cm.parentNode.removeChild(cm);
        log('コメント欄を非表示にしました（既存コメント無し）');
      } else {
        log('コメント投稿フォームを外しました（既存コメントは残す）: form', n, '個');
      }
      return;
    }

    // コンテナより後ろに既にあるなら何もしない
    if (cm.compareDocumentPosition(container) & Node.DOCUMENT_POSITION_PRECEDING) return;

    var host = document.createElement('div');
    host.className = 'infinite-article-comments';
    // 追記記事と同じラッパを付けて、移動先でも元のカラム幅・余白を保つ
    var inner = host;
    var wraps = Array.isArray(boot.comment_wrap_classes) ? boot.comment_wrap_classes : [];
    for (var wi = 0; wi < wraps.length; wi++) {
      if (typeof wraps[wi] !== 'string' || !wraps[wi]) continue;
      var d = document.createElement('div');
      d.className = wraps[wi];
      inner.appendChild(d);
      inner = d;
    }
    inner.appendChild(cm);
    // loader セクションの末尾（追記コンテナと可視リンクの後ろ）へ
    container.parentNode.appendChild(host);
    movedCommentForm = cm.querySelector('form[action*="/comments"]');
    log('コメント欄を追記記事の後ろへ移しました。ラッパ =', wraps.join(' ') || '(なし)',
        '/ フォーム =', !!movedCommentForm);
  })();

  /* ------------------------------------------------------------------ *
   * SP でサイドバーを一番後ろへ移す
   * ------------------------------------------------------------------ *
   * PC では本文の横に並ぶサイドバーが、SP ではテーマの media query で本文の
   * 下に積まれる（実測: 990px 以下で float 解除・width 100%）。
   * その結果、次の記事に着くまでサイドバーを通過することになる
   * （実測: 375px 幅で 5,179px）。コメント欄と同じ「壁」になるため、
   * SP のときだけ追記記事より後ろへ移す。
   * PC 幅に戻したら元の位置へ戻す（DOM 操作だけ / サーバ出力は変えない）。
   * ------------------------------------------------------------------ */
  var syncSidebar = null;   // DEBUG 用。SP/PC の判定をその場でやり直す
  var followSidebar = null; // DEBUG 用。追従パネルの表示/非表示を手で切り替える
  (function relocateSidebarOnMobile() {
    var sel = boot.sidebar_selector;
    if (typeof sel !== 'string' || !sel) return;
    var bp = parseInt(boot.sidebar_breakpoint, 10);
    if (!isFinite(bp) || bp < 320 || bp > 1600) return;
    if (!window.matchMedia) return;

    var bar;
    try { bar = document.querySelector(sel); } catch (e) { log('sidebar_selector が不正:', sel); return; }
    if (!bar || !bar.parentNode) return;

    // 戻すための元の位置を覚えておく
    var homeParent = bar.parentNode;
    var homeNext = bar.nextSibling;
    var host = null;
    var mq = window.matchMedia('(max-width: ' + bp + 'px)');

    function apply() {
      if (mq.matches) {
        if (host) return;
        host = document.createElement('div');
        host.className = 'infinite-article-sidebar';
        host.appendChild(bar);
        container.parentNode.appendChild(host);   // セクションの末尾＝追記記事より後ろ
        log('SP のためサイドバーを追記記事の後ろへ移しました');
      } else {
        if (!host) return;
        homeParent.insertBefore(bar, homeNext);   // 元の位置へ厳密に戻す
        if (host.parentNode) host.parentNode.removeChild(host);
        host = null;
        log('PC 幅に戻ったため、サイドバーを元の位置へ戻しました');
      }
    }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);   // Safari 13 以前
    // change が飛ばない環境（開発ツールの画面サイズ変更など）でも取りこぼさないよう、
    // resize でも同じ判定をやり直す。apply は冪等。
    window.addEventListener('resize', apply, { passive: true });
    syncSidebar = apply;
  })();

  /* ------------------------------------------------------------------ *
   * サイドバー追従（PC のみ / テーマ設定でオンのときだけ）
   *
   * `position: sticky` は **親のボックス内**でしか効かない。実測:
   *   .sub_second      高さ 5,245px
   *   .second_colume   min-height 3,500px（親。フロートを含まない）
   *   #infinite-article-container は .second_colume の **外**（別の枝）
   * したがって素の sticky は 3,500px で追従が止まる（実測でも scroll 43,294px の時点で
   * サイドバーの top が -43,233px = 画面外だった）。
   *
   * テーマの親子構造を変えずに追従させるため、**片端から N ブロックだけを複製して
   * position: fixed のパネルに入れ、元のサイドバーが画面から出たら表示する**。
   *   - サイドバー全体（5,245px）はビューポート（900px）より高く、貼り付けても
   *     下側が永久に見えない。上の数ブロックだけにするのはこのため。
   *   - 記事ごとにサイドバーを複製する案は、40本で 19,080ノード・画像2,640枚に
   *     なるので採らない（中身は全記事で同じ広告バナー）。
   * ------------------------------------------------------------------ */
  (function stickySidebar() {
    if (!boot.sidebar_sticky) return;
    // sticky_selector は 2カラム／1カラムでテンプレートごとに切り替わる（契約側で決める）。
    // 旧テーマとの互換で sidebar_selector にも落とす。
    var sel = boot.sticky_selector || boot.sidebar_selector;
    if (typeof sel !== 'string' || !sel) return;
    var bp = parseInt(boot.sidebar_breakpoint, 10);
    if (!isFinite(bp) || bp < 320 || bp > 1600) bp = 990;
    var topPx = parseInt(boot.sidebar_sticky_top, 10);
    if (!isFinite(topPx) || topPx < 0 || topPx > 400) topPx = 20;
    var blocks = parseInt(boot.sidebar_sticky_blocks, 10);
    if (!isFinite(blocks) || blocks < 0 || blocks > 6) blocks = 2;
    // どちらの端から取るか。上端は広告バナーが並ぶだけで違和感がある（ユーザー報告）ため
    // 既定は下端（記事一覧などが入る）。2026-08-04 ユーザー指示。
    var fromTop = boot.sidebar_sticky_from === 'top';
    if (!window.matchMedia) return;

    var bar;
    try { bar = document.querySelector(sel); } catch (e) { return; }
    if (!bar || !bar.children.length) return;

    var panel = null;
    var mq = window.matchMedia('(min-width: ' + bp + 'px)');

    // 追従させるブロックを **セレクタで名指し**する（設定が空なら端から N 個）。
    // 端から数える方式は、サイドバーの並びが変わると追従する中身も変わってしまう。
    // 出したいバナーが決まっているならこちらで固定する（2026-08-04 ユーザー要望）。
    // 書いた順に並べる。元サイドバーの中にある要素だけを対象にする
    //（別の場所の要素を引っぱってくると、元が画面に残っていても複製が出てしまう）。
    function pickBySelector() {
      var raw = boot.sidebar_sticky_selector;
      if (typeof raw !== 'string' || !raw.trim()) return null;
      var parts = raw.split(',');
      var out = [];
      for (var i = 0; i < parts.length; i++) {
        var q = parts[i].trim();
        if (!q) continue;
        var el = null;
        try { el = bar.querySelector(q); } catch (e) { el = null; }
        // id 指定はサイドバー配下でも document 側から引ける方が確実
        if (!el) { try { el = document.querySelector(q); } catch (e2) { el = null; } }
        if (el && bar.contains(el) && out.indexOf(el) === -1) out.push(el);
        else if (!el) log('サイドバー追従: 見つかりません', q);
      }
      return out.length ? out : null;
    }

    function build() {
      if (panel) return panel;
      panel = document.createElement('div');
      panel.className = 'infinite-sidebar-follow';
      panel.setAttribute('aria-hidden', 'true');   // 元のサイドバーと同じ内容の複製なので読み上げない
      var named = pickBySelector();
      if (named) {
        for (var k = 0; k < named.length; k++) panel.appendChild(named[k].cloneNode(true));
        document.body.appendChild(panel);
        log('サイドバー追従: 指定の', named.length, 'ブロックを複製しました');
        return panel;
      }
      var kids = bar.children;
      var n = blocks > 0 ? Math.min(blocks, kids.length) : kids.length;
      // cloneNode なので元の kids は減らない（live HTMLCollection でも添字は安定）
      var start = fromTop ? 0 : kids.length - n;
      for (var i = start; i < start + n; i++) panel.appendChild(kids[i].cloneNode(true));
      document.body.appendChild(panel);
      log('サイドバー追従:', fromTop ? '上から' : '下から', n, 'ブロックを複製しました');
      return panel;
    }

    // このテーマでは **window に scroll イベントが飛ばない**（実測: 39,918px まで
    // スクロールしても発火0回）。テーマ側が別の要素をスクロールさせているため。
    // そこで scroll に頼らず、元のサイドバーを IntersectionObserver で監視して
    // 画面から出入りしたときだけ切り替える。位置合わせは表示中だけ rAF で追う。
    // 記事の並びが終わったら、パネルを押し上げてフッターに被せない。
    // 素の position:fixed は最下部までスクロールしても居座るので、フッターの
    // サイトマップ（実測 1,374px）の上に広告が重なって見えていた（ユーザー報告）。
    // 止める線は **フッターではなく追記コンテナの下端**にする。
    // フッターのセレクタを設定で持たなくて済み、テーマが変わっても壊れない。
    function placeTop(p) {
      var top = topPx;
      if (container) {
        var limit = container.getBoundingClientRect().bottom;   // ビューポート座標
        var h = p.offsetHeight;
        if (top + h > limit) top = limit - h;                   // 下端に合わせて押し上げる
      }
      p.style.top = Math.round(top) + 'px';
    }
    var raf = 0;
    function follow() {
      raf = 0;
      if (!panel || panel.style.display === 'none') return;
      var r = bar.getBoundingClientRect();
      panel.style.left = Math.round(r.left) + 'px';
      panel.style.width = Math.round(r.width) + 'px';
      placeTop(panel);
      raf = requestAnimationFrame(follow);
    }
    function toggle(show) {
      if (!mq.matches) show = false;
      var p = show ? build() : panel;
      if (!p) return;
      var was = p.style.display;
      p.style.display = show ? 'block' : 'none';
      if (show) {
        p.style.position = 'fixed';
        var r = bar.getBoundingClientRect();
        p.style.left = Math.round(r.left) + 'px';
        p.style.width = Math.round(r.width) + 'px';
        placeTop(p);
        if (!raf) raf = requestAnimationFrame(follow);
      } else if (raf) {
        cancelAnimationFrame(raf); raf = 0;
      }
      if (was !== p.style.display) log('サイドバー追従:', show ? '表示' : '非表示');
    }
    if (!window.IntersectionObserver) return;
    // 元のサイドバーが画面から完全に出たら追従パネルを出す
    // （同じ広告が同時に2つ見えないようにするため）。
    new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) toggle(!entries[i].isIntersecting);
    }, { threshold: 0 }).observe(bar);
    followSidebar = toggle;   // DEBUG から手で呼べるようにする
    window.addEventListener('resize', function () { toggle(!!panel && panel.style.display === 'block'); },
                            { passive: true });
  })();

  // 投稿先の記事は action の URL だけで決まる（記事IDの hidden は無い）。
  // フォームを末尾へ移すと「読んでいる記事」と「投稿先」がずれるので、
  // 表示中の記事が切り替わるたびに action も付け替える。
  function retargetCommentForm(a) {
    if (!movedCommentForm || !a || !a.cleanUrl) return;
    try {
      var u = new URL(a.cleanUrl, window.location.href);
      if (u.origin !== window.location.origin) return;   // 別オリジンへは向けない
      movedCommentForm.setAttribute('action', u.pathname + '/comments#comment_form');
    } catch (e) { log('コメント投稿先の付け替えに失敗:', e && e.message); }
  }

  /* ------------------------------------------------------------------ *
   * S10: isTrusted な実操作の latch
   * ------------------------------------------------------------------ */
  function latch(ev) {
    if (state.latched) return;
    if (!ev || ev.isTrusted !== true) return;   // 合成イベント（click() / dispatchEvent）は無視
    state.latched = true;
    log('latched by', ev.type);
    removeLatchListeners();
    startObservers();
  }
  var latchEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown', 'click'];
  function addLatchListeners() {
    latchEvents.forEach(function (t) { window.addEventListener(t, latch, { passive: true, capture: true }); });
  }
  function removeLatchListeners() {
    latchEvents.forEach(function (t) { window.removeEventListener(t, latch, { capture: true }); });
  }
  addLatchListeners();

  /* ------------------------------------------------------------------ *
   * 監視（sentinel / primary-visible）
   * ------------------------------------------------------------------ */
  var sentinel = document.createElement('div');
  sentinel.className = 'infinite-article-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  // 高さ0の要素は IntersectionObserver が交差を報告しない場合があるため、実体を持たせる。
  // display も明示する。Dawn の base.css に `div:empty { display: none; }` があり、
  // 中身が空の sentinel はこれに当たって box を失う。そうなると IO は交差を報告せず、
  // スクロール判定の getBoundingClientRect() も全て 0 を返して常に成立してしまう。
  // インラインstyleはテーマのCSSより強いので、ここで指定するのが確実。
  sentinel.style.display = 'block';
  sentinel.style.height = '1px';
  sentinel.style.width = '100%';
  var sentinelIO = null;
  var primaryIO = null;
  var scrollCheckQueued = false;

  function startObservers() {
    container.parentNode.insertBefore(sentinel, container.nextSibling);

    if ('IntersectionObserver' in window) {
      sentinelIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          if (!readyForNextAppend()) { log('sentinel 到達。1本ずつ条件を満たすまで待機'); continue; }
          maybeLoadNext();
        }
      }, { rootMargin: CFG.sentinelRootMargin });
      sentinelIO.observe(sentinel);

      primaryIO = new IntersectionObserver(onPrimaryChange, { rootMargin: CFG.primaryRootMargin, threshold: 0 });
      state.articles.forEach(function (a) { if (a.el) primaryIO.observe(a.el); });
    } else {
      log('IntersectionObserver 非対応。スクロール判定のみで動作します');
    }

    prefetchListPages();

    // フォールバック: IO が発火しない環境／条件でも動くよう、スクロール位置でも判定する
    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.addEventListener('resize', onScrollCheck, { passive: true });
    onScrollCheck();   // 初回。sentinel が既に見えているならその場で読み込む
    log('observers started. sentinelInDom =', !!sentinel.parentNode);
  }

  function onScrollCheck() {
    if (scrollCheckQueued) return;
    scrollCheckQueued = true;
    requestAnimationFrame(function () {
      scrollCheckQueued = false;
      if (!state.latched) return;
      // 主可視判定（URL/title の復元）は読み込み条件と独立に、常に実行する。
      // sentinel は上限到達やチェーン終了で削除されるため、ここで return すると
      // 以降 URL が現在記事に追随しなくなる。
      checkPrimaryByRect();
      if (state.revoked || state.inFlight) return;
      if (!sentinel.parentNode) return;
      var r = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top <= vh + CFG.triggerMargin + CFG.appendPreloadPx && readyForNextAppend()) maybeLoadNext();
    });
  }

  // IntersectionObserver 非対応時の主可視判定（画面中央にある記事を current とする）
  function checkPrimaryByRect() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    // 「読み進め線」。この線を覆っている記事を表示中とみなす。
    // 中央（0.5）だと記事の真ん中まで読まないと URL・タイトルが切り替わらず、
    // 切り替わりが記事の途中で起きて見栄えが悪い。上寄りにすると、次の記事の
    // 見出しが画面上部へ来た時点で切り替わる（読者の体感と一致する）。
    var line = vh * CFG.primaryLineRatio;

    // 記事の「持ち場」は **自分の先頭から次の記事の先頭まで**とする。
    // 要素の box（bottom）で判定してはいけない。テーマが本文カラムのフロートを
    // 内包せず min-height だけ持っている場合、box が実際の表示範囲よりはるかに短い。
    //   実測（media/right-side の入口記事）: box 61〜3,561px に対し、本文は 61〜12,364px。
    // この差の区間ではどの記事も判定線を覆わず、「中心が線に近い記事」への
    // フォールバックが働いて、記事の途中で URL が飛んだり戻ったりしていた。
    // 追記側は clearfix で box を正確にしたが、入口記事は公開テーマのままなので直らない。
    // 先頭の座標だけを使えば、box の高さに一切依存しない。
    var found = null;
    var firstEl = null;
    for (var i = 0; i < state.articles.length; i++) {
      var a = state.articles[i];
      if (!a.el || !a.el.parentNode) continue;
      if (!firstEl) firstEl = a;
      // state.articles は DOM 順（入口 → 追記順）。先頭が線より下ならそれ以降も下。
      if (a.el.getBoundingClientRect().top > line) break;
      found = a;
    }
    // どの記事もまだ線に届いていない（ページ最上部）。先頭の記事を主可視とする。
    if (!found) found = firstEl;

    if (found && found !== state.current) {
      // 不感帯（上方向のみ）。下方向は従来どおり線ちょうどで切り替える。
      // 戻す条件は「現在記事の先頭が線より primaryBackMargin ぶん下まで降りたか」。
      // DOM 順 = 縦位置順なので、found が現在記事より上にあれば「戻り」と判定できる。
      var cur = state.current;
      if (cur && cur.el && cur.el.parentNode && CFG.primaryBackMargin > 0) {
        var curTop = cur.el.getBoundingClientRect().top;
        if (found.el.getBoundingClientRect().top < curTop &&
            curTop <= line + CFG.primaryBackMargin) {
          return;   // 不感帯の中。現在記事のまま据え置く（replaceState も PV も出さない）
        }
      }
      log('primary ->', found.id, found.cleanUrl);
      commitPrimary(found);
    }
  }

  // 1記事ずつ読み込むための条件。
  //   (1) 直前に追記した記事の「先頭」を通り過ぎている（= 実際にその記事を読み始めている）
  //   (2) 前回の追記から一定量スクロールしている
  // この2つを満たさない限り、sentinel が近くても次を読み込まない。
  var lastAppendScrollY = null;
  var retryTimers = [];          // 離脱時に解除する retry タイマー
  var teardownRequested = false; // pagehide 後は新しい処理を始めない
  var listAborter = null;        // list transport 用の AbortController
  // 追記は fetch 後に「画像寸法の解決 → 挿入位置待ち」と非同期に続く。
  // pagehide / BFCache をまたいで古い継続が動くと二重 append になるため、
  // 解除関数をここに集約して pagehide で必ず呼ぶ。
  var asyncCleanups = [];
  function registerCleanup(fn) { asyncCleanups.push(fn); return fn; }
  // 未登録（＝既に解除済み）なら何もしない。one-shot を保証する。
  function runCleanup(fn) {
    var i = asyncCleanups.indexOf(fn);
    if (i === -1) return false;
    asyncCleanups.splice(i, 1);
    try { fn(); } catch (e) {}
    return true;
  }
  function cleanupAllAsync() {
    while (asyncCleanups.length) { try { asyncCleanups.pop()(); } catch (e) {} }
  }

  // 追記後にどれだけ下方向へ進んだか。ページがそれ以上スクロールできない場合は
  // 要求量を「残りスクロール可能量」で clamp する（短い記事で詰まらないように）。
  function scrolledEnoughSinceAppend() {
    if (lastAppendScrollY === null) return true;
    var y = window.scrollY || window.pageYOffset || 0;
    var delta = y - lastAppendScrollY;                    // 下方向のみを進行とみなす
    var doc = document.documentElement;
    var maxScroll = Math.max(0, (doc.scrollHeight || 0) - (window.innerHeight || doc.clientHeight || 0));
    var available = Math.max(0, maxScroll - lastAppendScrollY);
    var required = Math.min(CFG.minScrollSinceAppend, available);
    return delta >= required;
  }

  // 直前に追記した記事を「読み終わりに近い」と言えるか。
  // 旧実装は「先頭が画面上端より上」だったが、viewport より短い記事では最下端まで
  // スクロールしても成立せず、2本目以降が永久に読み込めなくなるため変更した。
  function lastAppendedIsConsumed() {
    if (state.appended === 0) return true;
    var last = state.articles[state.articles.length - 1];
    if (!last || !last.el) return true;
    var r = last.el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.height === 0) return true;
    // 終端まで先読み距離以内 = 読み終わりに近い（ここから取得を始めてよい）。
    // 以前は「末尾が画面内」だったが、それだと取得を始めるのが終端の直前になり、
    // 取得（実測 248ms）が終わる前に読者が最下部へ着いてしまう。
    // sentinel の rootMargin と同じ距離にそろえ、どちらか一方だけが律速にならないようにする。
    if (r.bottom - vh <= CFG.triggerMargin + CFG.appendPreloadPx) return true;
    // consumeRatio ぶん読み進めたか（lazy=1.0 のときはこの条件を使わない）
    if (CFG.consumeRatio < 1.0) {
      var consumed = Math.min(1, Math.max(0, (vh - r.top) / Math.max(1, r.height)));
      if (consumed >= CFG.consumeRatio) return true;
    }
    return false;
  }

  function readyForNextAppend() {
    return scrolledEnoughSinceAppend() && lastAppendedIsConsumed();
  }

  /* ------------------------------------------------------------------ *
   * F3b: primary-visible → replaceState → title → PV
   * ------------------------------------------------------------------ */
  // IO の列挙順に依存すると、複数記事が中央バンドに同時交差したときに結果がぶれる。
  // 交差を「再評価のきっかけ」だけに使い、判定は常に距離評価へ委ねる。
  function onPrimaryChange() {
    checkPrimaryByRect();
  }

  // 表示URL（U1）。テーマ設定が ON のときだけ Forbes 型の ?n=&e= を付ける。
  // n = 入口記事から何本目か / e = 入口記事のID。どちらもサーバー側では読まない（Shopify の Liquid は
  // クエリを読めないため）。GA4 の標準レポートで無限スクロール由来の閲覧を分離する用途。
  function displayUrlFor(a) {
    if (!state.urlParams || !a.seq) return a.cleanUrl;
    try {
      var u = new URL(a.cleanUrl);
      u.searchParams.set('n', String(a.seq));
      u.searchParams.set('e', state.entryArticleHandle);
      return u.href;
    } catch (e) { return a.cleanUrl; }
  }

  function commitPrimary(a) {
    var prevTitle = document.title;

    // measurement_mode = 'auto' のとき、GA4 の「履歴イベントに基づくページビュー」は
    // replaceState の呼び出し時点で同期的に発火する。したがって **title を先に更新**しないと
    // 「URL は記事B / タイトルは記事A」の PV が送られる。失敗時は title を戻す（整合性は維持）。
    var titleFirst = (state.measurementMode === 'auto');
    if (titleFirst && a.title) { try { document.title = a.title; } catch (e) {} }

    // History。失敗したら title も PV も commit しない（F3b: fail-closed）
    if (historyEnabled) {
      try {
        history.replaceState(history.state, '', displayUrlFor(a));  // 既存 state を保存して破壊しない
      } catch (e) {
        log('replaceState failed; skip title/PV', e);
        if (titleFirst) { try { document.title = prevTitle; } catch (e2) {} }
        return;
      }
    }
    state.current = a;
    retargetCommentForm(a);

    // title（S7: title のみ例外）
    if (!titleFirst && a.title) { try { document.title = a.title; } catch (e) {} }

    // PV（F6: 1 document 1 article 1回）
    // - 'auto'   : GA4 の履歴PVに任せる。dataLayer へは何も送らない（二重計上の防止）
    // - 'manual' : dataLayer に送る。GA4 側の履歴PVは必ず OFF にすること
    // - 'off'    : 何も送らない（挙動確認フェーズ向け）
    if (state.measurementMode !== 'manual') return;
    var cur = state.pvState[a.id] || PV.NOT_ATTEMPTED;
    if (cur === PV.NOT_ATTEMPTED) {
      state.pvState[a.id] = PV.ENQUEUED;
      pushPageView(a);
    }
  }

  function pushPageView(a) {
    window.dataLayer = window.dataLayer || [];
    var prev = previousArticleOf(a);
    window.dataLayer.push({
      event: 'infinite_article_view',
      page_location: displayUrlFor(a),                 // U1: クリーンな絶対URL（設定が ON のときは ?n=&e= 付き）
      page_title: a.title || document.title,
      page_referrer: prev ? prev.cleanUrl : undefined,  // 直前の logical current article
      content_view_method: 'infinite',                  // 初期A は 'initial'（自動計測側）
      entry_article_id: state.entryArticleId,           // U3
      entry_article_handle: state.entryArticleHandle,   // レポートで記事を特定するため
      content_sequence_index: a.seq || 0                // U3（append 順）
    });
    log('dataLayer page_view', a.cleanUrl);
  }

  function previousArticleOf(a) {
    var i = state.articles.indexOf(a);
    return i > 0 ? state.articles[i - 1] : null;
  }
  function findArticleByEl(el) {
    for (var i = 0; i < state.articles.length; i++) if (state.articles[i].el === el) return state.articles[i];
    return null;
  }

  /* ------------------------------------------------------------------ *
   * F3: append フェーズ
   * ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ *
   * 方式B: タグ絞り込みページ（?view=infinite-list）からチェーンを作る
   * ------------------------------------------------------------------ */
  var listFetchState = 'idle';   // idle | fetching | done
  // 1タグあたり何ページまで一覧を掘るか（1ページ = 50件）の **暴走止め**。
  //
  // ここを「必要な深さ」に合わせて調整してはいけない。使える記事がどのページに
  // 現れるかは公開順で決まるので、**予測できないし記事が増えるたびに変わる**。
  // 実測（/blogs/media/tagged/python・全11ページ）:
  //     page1  既出の -output 22件 + 使える28件
  //     page2  curriculum 49 / right-side  1   ← ほぼ全部が除外テンプレート
  //     page3  curriculum 37                   ← 同上
  //     page4  right-side 48（column）         ← ここでやっと使える
  //     page5  right-side 50 / page6 right-side 50
  // 旧実装は上限3ページで、この「死んだ帯」の直前で諦めて次のタグ（cli）へ渡していた。
  // 帯の位置も幅もタグごとに違うため、固定ページ数では必ずどこかで同じことが起きる。
  //
  // そこで **チェーンが埋まるまで・ページが残る限り・通信量の予算内で掘る**方式にし、
  // ここは「万一終わらないときに止めるための上限」だけを担う（既定20＝1,000件）。
  // tagged 一覧は記事数ぶん最後まで辿れる（実測: kids タグは pages=233、
  // page=200 でも 50件返る。1,000件で打ち切られるといった制限は無い）。
  var LIST_MAX_PAGES = (function () {
    var n = parseInt(boot.list_max_pages, 10);
    return (isFinite(n) && n >= 1 && n <= 30) ? n : 20;
  })();
  // 「絞り込みで1件も拾えないページ」が何回続いたら見切るか。
  // 1回で見切ると上の page2〜3 のような死んだ帯を越えられない。
  // 見切りを残すのは、当たる見込みの無いソースで最大ページ数まで取得しないため
  //（実測: school-detail は column グループが空で 1本目まで 2.7 秒・一覧通信 15 回）。
  var LIST_EMPTY_STREAK_MAX = 3;
  // 追記候補を入口記事と同じテンプレートに限定するか。
  // タグは概ねテンプレートごとに分かれているが、完全ではない
  // （実測: right-side の「教育」タグは right-side 242 / main-column 131 /
  //  right-side-kids 22 / curriculum 1 を含む）。体裁を揃えたいテンプレートでは
  // タグ任せにせず明示的に絞る。
  var entryTemplate = String(boot.assigned_article_template_suffix || '');
  var templateLocked = false;
  (function applyTemplateLock() {
    var raw = boot.same_template_only;
    if (typeof raw !== 'string' || !raw) return;
    var list = raw.split(',');
    for (var i = 0; i < list.length; i++) {
      if (list[i].trim() === entryTemplate) { templateLocked = true; return; }
    }
  })();

  var listCandidates = [];       // このタグから集めた候補（rank = 新しい順の通し番号）
  // 取得済みページ番号。ページ予算は「この配列の長さ」で数える。カウンタを別に持つと
  // pagehide → BFCache 復帰で同じページを取り直したときに二重計上される（8巡目の指摘）。
  var listPagesSeen = [];
  var listPendingPage = 0;       // 取得中（= 中断され得る）ページ。復帰時はここから再開する
  var deepPageAttempted = false;  // 深いページの覗きは1ソースにつき1回だけ
  var listEmptyStreak = 0;        // 絞り込みで1件も拾えないページが何回続いたか
  // 選抜済みソースの index。boolean を reset 対象に置くと、正常終了のたびに
  // resetListSourceState() が false へ戻してしまい印にならなかった（10巡目の指摘）。
  // ソース index に紐付ければ、次ソースへ進んだ時点で自然に解除される。
  var finalizedSourceIdx = -1;
  var lastListDebug = null;      // DEBUG: 直前に確定したソースの内訳（reset 前の写し）
  var listSourceIdx = 0;         // 候補タグの何番目を試しているか
  var listToken = null;          // 実行中の list リクエストの識別子（stale 継続の遮断用）
  var listSources = (boot.list_sources && boot.list_sources.length) ? boot.list_sources : [];

  /* ------------------------------------------------------------------ *
   * 公開日の帯（band）で追記順を制御する
   * ------------------------------------------------------------------ *
   * 既定は「タグ順が第1キー」＝1つ目のタグを使い切ってから2つ目へ。
   * これだと1つ目のタグの古い記事が、2つ目のタグの新しい記事より先に出る。
   * 帯を有効にすると **帯が第1キー・タグ順が第2キー** になる:
   *     1つ目のタグの1年以内 → 2つ目の1年以内 → 3つ目の1年以内
   *   → 1つ目のタグの1〜2年 → 2つ目の1〜2年 → …
   *
   * 実装は listSources を (タグ × 帯) に展開するだけ。1ソース＝1タグ1帯なので、
   * 既存の「1ソースずつ使い切って次へ」という機構をそのまま使える。
   * 同じタグを帯の数だけ引くことになるので、応答はURL単位でキャッシュする。
   *
   * 対象テンプレートを絞れるようにしてある。実測で main-column は
   * 11,673件中 11,623件（99.6%）が同じ帯なので、展開しても意味がなく
   * ソース数だけ増える。
   * ------------------------------------------------------------------ */
  /* handle によるグループ優先。帯より上位のキーになる。
     設定例 "column,*,news-prt-" → 0:handleにcolumnを含む / 1:その他 / 2:PR TIMES
     `*` は「残り全部」の置き場所。より後ろの具体パターンが優先される。 */
  // カンマ区切りのテーマ設定を配列にする（空要素は落とす）
  function splitSetting(raw, lower) {
    if (typeof raw !== 'string' || !raw) return [];
    var out = [];
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var v = parts[i].trim();
      if (v) out.push(lower ? v.toLowerCase() : v);
    }
    return out;
  }

  var handleOrder = [];
  (function applyHandleOrder() {
    var raw = boot.handle_order;
    if (typeof raw !== 'string' || !raw) return;
    var parts = raw.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim().toLowerCase();
      if (p) out.push(p);
    }
    // グループが1つしかないなら並べ替える意味がない
    if (out.length < 2 || out.length > 5) return;
    handleOrder = out;
  })();

  function groupOfHandle(h) {
    if (!handleOrder.length) return 0;
    var s = String(h == null ? '' : h).toLowerCase();
    var wildcard = -1;
    for (var i = 0; i < handleOrder.length; i++) {
      if (handleOrder[i] === '*') { wildcard = i; continue; }
      if (s.indexOf(handleOrder[i]) !== -1) return i;
    }
    // `*` が無い設定でどれにも当たらないものは最後（既知のグループを汚さない）
    return wildcard >= 0 ? wildcard : handleOrder.length;
  }

  // 「ニュース系」と見なす handle の部分文字列（テーマ設定 band_groups）。
  // 帯を使うかどうかの判定と、古い候補を落とす判定の両方で使う。
  var newsPatterns = [];
  (function applyNewsPatterns() {
    var raw = boot.band_groups;
    if (typeof raw !== 'string' || !raw.trim()) return;
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim().toLowerCase();
      if (p) newsPatterns.push(p);
    }
  })();

  function handleIsNews(h) {
    if (!newsPatterns.length) return false;
    var s = String(h == null ? '' : h).toLowerCase();
    for (var i = 0; i < newsPatterns.length; i++) {
      if (s.indexOf(newsPatterns[i]) !== -1) return true;
    }
    return false;
  }

  var bandEdges = [];            // 単位は「月」。例 [3,6,12,24] → 5帯（〜3ヶ月 / 3〜6 / 6〜12 / 1〜2年 / 2年以上）
  (function applyBands() {
    var tpls = boot.band_templates;
    if (typeof tpls !== 'string' || !tpls) return;
    var ok = false;
    var tl = tpls.split(',');
    for (var i = 0; i < tl.length; i++) if (tl[i].trim() === entryTemplate) ok = true;
    if (!ok) return;

    // 帯（公開日）を使うのは **入口記事がニュース系のときだけ**（2026-08-03 ユーザー判断）。
    //   「columnやterminologyは期間が関係ない。公開日が大きく関係するのは
    //     PR TIMES などのニュースが主となる」
    // コラム記事に帯を当てると、Git の解説記事の間に「新しい帯の PR TIMES」が
    // 割り込む（実測: column407 の2本目に【ボーンデジタル】…が入った）。
    // 帯を使わないときは tier → グループ → タグ の順になり、
    // 候補タグの並び（＝関連性順）がそのまま出る。
    if (newsPatterns.length &&
        !handleIsNews((boot.initial_article && boot.initial_article.handle) || '')) return;

    var raw = boot.band_months;
    if (typeof raw !== 'string' || !raw) return;
    var parts = raw.split(',');
    for (var j = 0; j < parts.length; j++) {
      var v = parseFloat(parts[j]);
      // 昇順・正の数だけ受ける。壊れた設定なら帯を使わない（fail closed）
      if (!isFinite(v) || v <= 0) return;
      if (bandEdges.length && v <= bandEdges[bandEdges.length - 1]) return;
      bandEdges.push(v);
    }
    // 帯が多すぎるとタグ×帯のソース数が跳ね、空振りのソースばかりになる
    if (bandEdges.length > 8) { bandEdges = []; return; }
    if (!bandEdges.length || !listSources.length) { bandEdges = []; return; }
  })();

  // 追記候補にしないテンプレート（テーマ設定）。
  // 講座（curriculum）は本文がセクションで組まれていて article.content が空なので、
  // 取りに行っても transport 側で不適格になり、1回ぶんの通信を捨てるだけになる。
  // 一覧の tpl で分かるので **取りに行く前に**落とす。
  // 実測（2026-08-03 ユーザー報告）: git タグの一覧に cursor-curriculum34〜36 が並び、
  // 「Cursor講座 7.2〜7.4」が候補に上がっていた。
  var excludeTemplates = [];
  (function applyExcludeTemplates() {
    var raw = boot.exclude_templates;
    if (typeof raw !== 'string' || !raw) return;
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (p) excludeTemplates.push(p);
    }
  })();

  function templateIsExcluded(tpl) {
    if (!excludeTemplates.length) return false;
    var t = String(tpl == null ? '' : tpl);
    for (var i = 0; i < excludeTemplates.length; i++) {
      if (excludeTemplates[i] === t) return true;
    }
    return false;
  }

  // 古いニュースを候補から落とす（2026-08-03 ユーザー指示）。
  //   「公開日は2024年8月21日。今回のタグ更新で更新日が2026年8月2日になっているが、
  //     内容的には2024年8月21日なので出したくない。更新日ではなく公開日で縛る」
  // 判定は **入口記事ではなく候補記事の種類** で行う。コラムを入口にしたときも
  // 2年前のニュースが混ざるため（実測: column407 の11本目に 2024-08-20 の
  // 「Visual Studio 2022 v17.11…」が出た）。
  // 一覧が渡す `pub` は published_at（更新日ではない）。テンプレート側で
  // `a.published_at | date: '%s'` を出しているので、タグを更新しても値は動かない。
  // コラム・用語解説は古くても価値が変わらないので対象にしない。
  function candidateTooOld(it) {
    var m = parseFloat(boot.band_max_months);
    if (!isFinite(m) || m <= 0) return false;
    if (!handleIsNews(it && it.handle)) return false;
    var pub = it && it.pub;
    if (!Number.isFinite(pub) || pub <= 0) return true;   // 公開日が読めないニュースは出さない
    return (Date.now() / 1000 - pub) / SEC_PER_MONTH > m;
  }

  // ニュース系で「N ヶ月以内の記事しか出さない」ための上限（帯の本数に変換する）。
  // 0 / 未設定なら制限なし。帯を使わない入口記事（コラム）では意味を持たない。
  // 実測（2026-08-03 / news 系 8,011件の候補数中央値）:
  //   1ヶ月以内 26本（30% が10本未満）/ 3ヶ月以内 73本（9%）/ 制限なし 165本
  // 1ヶ月だと3割の記事で候補が10本を切るため、既定は 3ヶ月。
  var bandLimit = bandEdges.length + 1;
  (function applyBandWindow() {
    if (!bandEdges.length) return;
    var m = parseFloat(boot.band_max_months);
    if (!isFinite(m) || m <= 0) return;
    var n = 0;
    for (var i = 0; i < bandEdges.length; i++) if (bandEdges[i] <= m) n += 1;
    bandLimit = Math.max(1, n);       // 0本になると1本も追記できないので最低1帯は残す
    log('追記候補を', m, 'ヶ月以内に制限します（使う帯', bandLimit, '/', bandEdges.length + 1, '）');
  })();

  // ソースを tier × 帯 × グループ × タグ に展開する。
  // 並び順の優先度は **tier → 帯 → グループ → タグ順 → 新着重み**。
  //   tier0(主題タグ) 帯0(〜3ヶ月): column → その他 → PR TIMES
  //   tier0(主題タグ) 帯1(〜6ヶ月): column → その他 → PR TIMES
  //   …
  // グループを帯より上位にすると、古い column が新しい記事より先に出てしまい
  // 「3ヶ月区切りで新しい順」という前提が壊れる（実測: 20本すべて column になった）。
  // 1ソース＝1(帯,グループ,タグ) なので、既存の「1ソースずつ使い切って次へ」の
  // 機構をそのまま使える。同じタグの応答はキャッシュするので通信は増えない。
  (function expandSources() {
    if (!listSources.length) return;
    if (!bandEdges.length && !handleOrder.length) return;
    var groups = handleOrder.length ? handleOrder.length : 1;
    var bands = bandEdges.length + 1;
    var base = listSources;
    // tier を **帯より外側** の第1キーにする。
    // tier 0 = infinite_tag（記事の主題として選んだ候補）/ tier 1 = article.tags（保険）。
    // 帯を最外にすると「新しい帯の magazine-list」が「古い帯の Git」より先に来てしまい、
    // 2記事目から主題と無関係な記事が出る（2026-08-03 実測・column407）。
    // 主題タグを全帯ぶん使い切ってから保険タグへ進むのが正しい順序。
    var tiers = [];
    for (var t0 = 0; t0 < base.length; t0++) {
      var tv = base[t0].tier;
      if (typeof tv !== 'number' || !isFinite(tv) || tv < 0) tv = 0;
      if (tiers.indexOf(tv) === -1) tiers.push(tv);
    }
    tiers.sort(function (x, y) { return x - y; });
    function tierOf(s) {
      var v = s.tier;
      if (typeof v !== 'number' || !isFinite(v) || v < 0) return 0;
      return v;
    }
    var expanded = [];
    for (var t = 0; t < tiers.length; t++) {
      if (bandEdges.length) {
        // ニュース系の入口記事: 公開日が意味を持つので **帯が第1キー**。
        //   tier → 帯 → グループ → タグ
        for (var b = 0; b < bands && b < bandLimit; b++) {
          for (var g = 0; g < groups; g++) {
            for (var s = 0; s < base.length; s++) {
              if (tierOf(base[s]) !== tiers[t]) continue;
              expanded.push({ tags: base[s].tags, url: base[s].url,
                              band: b, group: g, tier: tiers[t] });
            }
          }
        }
      } else {
        // コラム・用語解説: 公開日は関係ないので **タグが第1キー**。
        //   tier → タグ → グループ
        // グループをタグより外側にすると、Git(25件) の column ぶん9本を出した時点で
        // 次のタグ（ブランチ）の column へ移ってしまい、10本目に Figma の記事が出た
        // （2026-08-03 実測・ユーザー報告）。Git の残り16本は terminology 系＝
        // 「その他」グループなので、後回しにされていた。
        // メインキーワードを最優先にするなら、**1つのタグを全グループぶん使い切ってから**
        // 次のタグへ進むのが正しい。グループの優先（column → その他 → PR TIMES）は
        // タグの中で効く。
        for (var s2 = 0; s2 < base.length; s2++) {
          if (tierOf(base[s2]) !== tiers[t]) continue;
          for (var g2 = 0; g2 < groups; g2++) {
            expanded.push({ tags: base[s2].tags, url: base[s2].url,
                            band: 0, group: g2, tier: tiers[t] });
          }
        }
      }
    }
    listSources = expanded;
    log('並べ替え: tier', tiers.length,
        bandEdges.length ? '→ 帯 → グループ → タグ' : '→ タグ → グループ（帯なし）',
        '/ 帯', bands, '/ グループ', groups, '/ タグ', base.length,
        '= ソース', expanded.length,
        '/ 帯の境目(月) =', bandEdges.join(',') || '(なし)',
        '/ グループ順 =', handleOrder.join(' → ') || '(なし)');
  })();

  var SEC_PER_MONTH = 30.44 * 24 * 3600;   // 365.25 / 12
  // 公開日が無い・壊れている項目は最後の帯へ落とす（早い帯を汚さない）
  function bandOfPub(pub) {
    if (!bandEdges.length) return 0;
    if (!Number.isFinite(pub) || pub <= 0) return bandEdges.length;
    var months = (Date.now() / 1000 - pub) / SEC_PER_MONTH;
    for (var i = 0; i < bandEdges.length; i++) if (months < bandEdges[i]) return i;
    return bandEdges.length;
  }

  // handle ブロックリスト。Liquid 側でも除外しているが、二重で守る。
  // 比較対象は Liquid と同じ「記事 slug」（article.handle の最後のパス要素）。
  // URL.pathname と比べてはいけない: pathname には locale prefix と blog handle が
  // 含まれ（/ja/blogs/news/...）、さらに日本語が percent-encode されるため、
  // `blogs` のようなパターンが全記事に当たり、日本語パターンは永久に当たらない。
  var handleBlocklist = [];
  if (Array.isArray(boot.handle_blocklist)) {
    for (var hb = 0; hb < boot.handle_blocklist.length; hb++) {
      var pat = String(boot.handle_blocklist[hb] == null ? '' : boot.handle_blocklist[hb])
                  .trim().toLowerCase();
      if (pat) handleBlocklist.push(pat);
    }
  }
  // handle が無い応答は「照合できない」= 除外側に倒す（fail closed）。
  // ブロックリストが空なら照合自体が不要なので、その場合だけ通す。
  function handleIsBlocked(handle) {
    if (!handleBlocklist.length) return false;
    if (typeof handle !== 'string' || !handle) return true;
    var h = handle.trim().toLowerCase();
    if (!h) return true;
    for (var i = 0; i < handleBlocklist.length; i++) {
      if (h.indexOf(handleBlocklist[i]) !== -1) return true;
    }
    return false;
  }

  // 次の候補タグへ進む。候補が尽きたら false
  function resetListSourceState() {
    listCandidates = [];
    listPagesSeen = [];
    listPendingPage = 0;
    listEmptyStreak = 0;
    deepPageAttempted = false;
  }

  // このソースをもう1ページ掘ってよいか。
  // 深さを固定値で決めず、**予算**で決める（上のコメント参照）。
  //   - 一覧をもう1ページ取っても、そのあと本文を1本取る余地が残ること
  //   - 暴走止めの上限に達していないこと
  function canDigDeeper() {
    if (listPagesSeen.length >= LIST_MAX_PAGES) return false;
    // 予算判定は listAdmission に集約した（先読み・通常・補充で同じ関数を通す）。
    // 一覧だけで累積予算を食い潰さないための一覧専用枠と、本文1本ぶんの確保を含む。
    return listAdmission('dig');
  }
  function advanceListSource() {
    listSourceIdx += 1;
    resetListSourceState();
    if (listSourceIdx >= listSources.length) return false;
    listFetchState = 'idle';
    return true;
  }
  function currentListSource() { return listSources[listSourceIdx] || null; }
  // 一覧の1ページ目URL（キャッシュのキーと同じ形）
  function listPageOneUrl(sourceUrl) {
    try {
      var u = new URL(sourceUrl);
      u.searchParams.set(CFG.viewParam, 'infinite-list');
      u.searchParams.delete('page');
      return u.href;
    } catch (e) { return null; }
  }

  // 取得済みの1ページ目から「このソース（タグ×帯×グループ）には該当が無い」と
  // 判断できるなら true。通信も処理もせず飛ばすために使う。
  //
  // 例: prna 記事の入口では、どのタグを引いても handle は prna-list* なので
  //     column グループには絶対に当たらない。従来はタグごとに取得して
  //     空振りを確認していた（実測: 1本目まで 3.9 秒 / 一覧通信 17 回）。
  //
  // 未取得のタグでは判断しない（false を返して通常どおり取得する）。
  function sourceLooksEmpty(src) {
    if (!src) return false;
    if (!bandEdges.length && !handleOrder.length) return false;
    if (listPendingPage > 1) return false;          // 中断ページからの再開中は触らない
    var href = listPageOneUrl(src.url);
    if (!href) return false;
    var c = listContractCache[href];
    if (!c || !Array.isArray(c.items) || !c.items.length) return false;

    var sawOlder = false;
    for (var i = 0; i < c.items.length; i++) {
      var it = c.items[i];
      if (!it) continue;
      var bandOk = !bandEdges.length || bandOfPub(it.pub) === src.band;
      var groupOk = !handleOrder.length || groupOfHandle(it.handle) === src.group;
      if (bandOk && groupOk) return false;          // 1件でも当たるなら空ではない
      if (bandEdges.length && bandOfPub(it.pub) > src.band) sawOlder = true;
    }
    // 帯を使っている場合は「対象の帯より古い記事が見えた＝この帯は通り過ぎた」ときだけ
    // 空だと断定できる（一覧は新しい順なので、深いページにも対象の帯は無い）。
    if (bandEdges.length) return sawOlder;
    // 帯を使わない場合はグループだけの判定。1ページ目に1件も無ければ空とみなす。
    // 深いページにだけ存在するケースを取りこぼすが、そもそも取得後の判定でも
    // 同じソースは捨てているので結果は変わらない（速くなるだけ）。
    return true;
  }

  function tryNextListSource() {
    // 最初の走査だけ先読みの完了を待つ。待たずに走ると、まだキャッシュに乗っていない
    // タグを1本ずつ直列に取りに行くことになり、先読みの意味がほぼ無くなる
    // （実測: 待たない場合 1本目まで 1.6〜2.0 秒 / 一覧通信 7〜8 回）。
    if (prefetchPromise && !prefetchWaited) {
      prefetchWaited = true;
      var pending = prefetchPromise;
      prefetchPromise = null;
      log('先読みの完了を待ちます');
      pending.then(function () { tryNextListSource(); },
                   function () { tryNextListSource(); });
      return;
    }
    var src = currentListSource();
    // 取得済みの情報だけで空と分かるソースは、通信せずに飛ばす
    var skipped = 0;
    while (src && sourceLooksEmpty(src)) {
      skipped += 1;
      if (!advanceListSource()) { finishListPhase(); return; }
      src = currentListSource();
    }
    if (skipped) log('取得済みの一覧から空と判断して飛ばしました:', skipped, 'ソース');
    if (!src) { finishListPhase(); return; }
    log('タグ候補を試行:', src.tags, listPendingPage ? ('（中断ページ ' + listPendingPage + ' から再開）') : '');
    fetchListChain(src.url, listPendingPage || 1);
  }

  // このソースが失敗したときの共通処理。候補があるなら捨てずに確定し、
  // 無いなら次のソースへ進む。catch と URL 不正の両方から呼ぶ。
  function failCurrentListSource(reason) {
    listFetchState = 'done';
    if (listCandidates.length) { finalizeCurrentListSource(reason || 'fetch failed'); return; }
    listPendingPage = 0;
    if (advanceListSource()) { tryNextListSource(); return; }
    finishListPhase();
  }

  // このソースの取得を終える唯一の出口。**必ずここで選抜する**。
  // 後続ページの失敗や空ページで advanceListSource() へ直行すると、
  // 1ページ目で集め終えた候補を丸ごと捨てることになる（8巡目の指摘）。
  function finalizeCurrentListSource(reason) {
    // guard は state を書き換える**前**に置く（書き換えてから return すると、
    // 二重呼び出しが listFetchState / listPendingPage だけ動かしてしまう）。
    if (finalizedSourceIdx === listSourceIdx || !currentListSource()) {
      log('finalize 二重呼び出しを無視:', reason);
      return;
    }
    finalizedSourceIdx = listSourceIdx;
    listFetchState = 'done';
    listPendingPage = 0;
    var added = selectFromCandidates();
    lastListDebug = {
      reason: reason, added: added, sourceIdx: listSourceIdx,
      tags: currentListSource() ? currentListSource().tags : null,
      pagesSeen: listPagesSeen.slice(), pool: listCandidates.length,
      deepPageAttempted: deepPageAttempted,
      ranks: listCandidates.map(function (x) { return x.rank; })
    };
    log('list -> chain', { added: added, chainLen: state.chain.length,
                           pool: listCandidates.length, reason: reason });
    if (state.chain.length < state.maxAppends) {
      if (advanceListSource()) { tryNextListSource(); return; }
    }
    resetListSourceState();
    finishListPhase();
  }
  function finishListPhase() {
    listFetchState = 'done';
    if (state.chain.length) maybeLoadNext();
    else stop('タグ候補をすべて試しましたが候補記事がありません');
  }

  // 集めた候補から必要本数を選ぶ。
  //
  // order_mode='fixed'（既定・2026-08-03 ユーザー判断）:
  //   **公開日が新しい順に上から取る**。同じ記事を開けば誰でも同じ並びになる。
  //   一覧は published_at の降順で返ってくるので、rank 昇順 = 新しい順。
  // order_mode='weighted'（従来）:
  //   weight = 1/(rank+1)^recencyExponent の重み付き非復元抽出。
  //   新しい記事ほど選ばれやすいが古い記事にも確率が残るので、読む人ごとに変わる。
  //   代わりに「必ず新しい順」にはならない。
  var selectedRanks = [];        // DEBUG 用。実際に選ばれた rank
  function selectFromCandidates() {
    var need = state.maxAppends - state.chain.length;
    if (need <= 0 || !listCandidates.length) return 0;
    var pool = listCandidates.slice();
    var picked = [];
    if (CFG.orderMode === 'fixed') {
      pool.sort(function (a, b) { return a.rank - b.rank; });
      // ぴったり need 本だけ選ぶと、**本文取得で不適格と分かった分が次のタグで埋まる**。
      // 在庫のあるタグを飛ばして関連の薄い記事が混ざるので、予備を上乗せして選ぶ。
      // 実測（/blogs/media/python-argparse-json-output・40本）:
      //   1〜21 python-output（22件を出し切り）/ 22〜38 python / 39〜40 cli
      //   python は 480件あるのに 17本で終わり、不足2本が cli から入っていた
      //   （need 19 に対し2本が不適格だったため）。
      // 上乗せは有限にする（ソースが丸ごと不適格でも次のタグへ渡る道を残すため）。
      var spare = Math.min(10, Math.max(2, Math.ceil(need * 0.5)));
      picked = pool.slice(0, need + spare);
    } else {
      while (picked.length < need && pool.length) {
        var total = 0, i;
        for (i = 0; i < pool.length; i++) total += pool[i].weight;
        var idx = pool.length - 1;
        if (total > 0) {
          var r = Math.random() * total, acc = 0;
          for (i = 0; i < pool.length; i++) {
            acc += pool[i].weight;
            if (r <= acc) { idx = i; break; }
          }
        } else {
          idx = Math.floor(Math.random() * pool.length);
        }
        picked.push(pool.splice(idx, 1)[0]);
      }
    }
    picked.sort(function (a, b) { return a.rank - b.rank; });
    for (var k = 0; k < picked.length; k++) {
      selectedRanks.push({ sourceIdx: listSourceIdx,
                           tags: currentListSource() ? currentListSource().tags : null,
                           rank: picked[k].rank, articleId: picked[k].article_id });
    }
    for (k = 0; k < picked.length; k++) {
      state.chain.push({
        article_id: picked[k].article_id, clean_url: picked[k].clean_url,
        // handle も引き継ぐ。落とすと handle パターンで判定する処理
        //（本文スクリプトの実行可否など）が常に不成立になる。
        title: picked[k].title, handle: picked[k].handle, eligibility: true
      });
    }
    return picked.length;
  }

  // まだ取得していないページ番号を選ぶ。前方（新しい側）に寄せる。
  function pickDeepPage(pages) {
    if (pages < 2) return 0;
    for (var tries = 0; tries < 12; tries++) {
      var p = 2 + Math.floor(Math.pow(Math.random(), CFG.pageBiasExp) * (pages - 1));
      if (p > pages) p = pages;
      if (listPagesSeen.indexOf(p) === -1) return p;
    }
    return 0;
  }

  // 予算超過は「このタグが失敗した」ではなく list フェーズ全体の停止として扱う。
  // 通常の失敗と同じ catch に入れると、予算を使い切った後も次のタグを取得してしまう。
  function budgetExceeded(msg) { var e = new Error(msg); e.__budget = true; return e; }

  // 一覧応答のキャッシュ。帯やグループを使うと同じタグを何度も引くので、
  // 同一URLの再取得を避ける（通信量とレート制限の両方に効く）。
  // **パース済みの契約オブジェクト**を持つ。HTML の再パース（DOMParser）は
  // 50KB 級の応答で無視できないコストになり、ソース数が3桁になると
  // メインスレッドを塞いで INP を悪化させるため。
  var listContractCache = Object.create(null);
  // 取得中の一覧（URL -> Promise）。先読みと本処理が同じURLを二重に取りに行くのを防ぐ。
  // 二重取得すると、先読みの意味が無いうえレート制限にも近づく。
  var listInflight = Object.create(null);
  var listContractCacheKeys = [];
  var LIST_CACHE_MAX = 24;
  var listNetworkFetches = 0;
  // 一覧に使った累計バイト。**本文とは別枠**で持つ。
  // 深く掘れるようにした結果、一覧が累積予算を食って本文が入らなくなる経路ができた
  //（実測の上限値: 一覧は最大 48回 × 300KB = 14,400KB で、累積上限 8,000KB を超える）。
  // 一覧を掘るかどうかはこちらで判定し、本文用の予算を必ず残す。
  var listBytes = 0;
  // **取得中**の一覧ぶんの予約。完了済みバイトだけで判定すると、
  // 先読み6件が同時に走ったとき全部が「まだ 0 バイト」の状態で通ってしまい、
  // 6 × 300KB = 1,800KB を上限判定なしで消費し得る（2巡目 Codex 指摘）。
  var listBytesReserved = 0;

  // 一覧の実通信を始めてよいか。**先読みも通常経路も必ずここを通す**。
  //   - 通信回数の全体上限
  //   - 一覧専用の累計バイト（取得中の予約を含む）
  //   - 本文1本ぶんを残した全体の累積バイト
  // prefetch=true のときは、前景（実際に必要なソース）用の枠を1件ぶん必ず残す。
  //   kind: 'prefetch' | 'first' | 'dig'
  // 戻り値は **拒否理由**。'' なら通す。
  //   'fetches' … 通信回数の上限（従来どおり「このソースの失敗」として扱う）
  //   'bytes'   … バイト予算（list フェーズ全体の停止として扱う。次のタグを引いても同じなので）
  function listAdmissionReason(kind) {
    var want = CFG.maxListBytes;
    // 通信回数。先読みは前景ぶんを1枠残す（3巡目 Codex 指摘: 予約が
    // バイトの軸だけで、回数の軸では前景を締め出し得た）。
    var slots = (kind === 'prefetch') ? 1 : 0;
    if (listNetworkFetches + slots >= CFG.maxListFetches) return 'fetches';
    // 一覧専用の累計バイト（取得中の予約を含む）。先読みは前景ぶんを1件残す。
    var used = listBytes + listBytesReserved;
    var reserve = (kind === 'prefetch') ? want * 2 : want;
    if (used + reserve > CFG.maxListBytesCumulative) return 'bytes';
    // 全体の累積バイト。**どの経路でも最低限「その一覧が入る余地」は要求する**
    //（3巡目 Codex 指摘: first だけ素通りしていて、上限超過を受信後に捨てるしかなかった）。
    if (state.bytes + listBytesReserved + want > CFG.maxBytesCumulative) return 'bytes';
    // さらに「本文1本ぶんを残す」確保は、掘るときと先読みにだけ課す。
    // ソースの1ページ目まで拒むと候補が1件も作れなくなるため。
    if (kind === 'first') return '';
    return (state.bytes + listBytesReserved + want + CFG.maxBytesPerResponse
            <= CFG.maxBytesCumulative) ? '' : 'bytes';
  }
  function listAdmission(kind) { return listAdmissionReason(kind) === ''; }

  function fetchListContract(href, ac, token, kind) {
    // キャッシュは **帯／グループが有効なときだけ** 使う。
    // それ以外の構成では同じURLを2度引くこと自体が異常（中断ページからの再開が
    // 壊れている等）なので、キャッシュで隠すと不具合が見えなくなる。
    // 実際、無条件キャッシュにしたらミューテーション検査の
    // 「常に1ページ目から取り直す」変異が検出できなくなった。
    var useCache = bandEdges.length > 0 || handleOrder.length > 0;
    var hit = useCache ? listContractCache[href] : null;
    if (hit) { log('list キャッシュ命中', href); return Promise.resolve({ c: hit, cached: true }); }
    if (useCache && listInflight[href]) {
      log('list 取得中に合流', href);
      return listInflight[href].then(function (c) { return { c: c, cached: true }; });
    }
    var why = listAdmissionReason(kind || 'first');
    if (why) {
      // バイト予算切れは「このタグの失敗」ではなく list フェーズ全体の停止。
      // 次のタグを引いても同じ理由で弾かれるので、進めても通信を無駄にするだけ。
      return Promise.reject(why === 'bytes'
        ? budgetExceeded('list byte budget')
        : new Error('list fetch budget (' + CFG.maxListFetches + ')'));
    }
    listNetworkFetches += 1;
    // 完了（成功でも失敗でも）まで予約を握る。握らないと並行取得で上限をすり抜ける。
    listBytesReserved += CFG.maxListBytes;
    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      listBytesReserved -= CFG.maxListBytes;
      if (listBytesReserved < 0) listBytesReserved = 0;
    };
    return fetch(href, {
      method: 'GET', mode: 'same-origin', credentials: 'same-origin',
      redirect: 'error', cache: 'no-store', signal: ac.signal,
      headers: { 'Accept': 'text/html' }
    }).then(function (res) {
      if (listToken !== token) throw new Error('stale list continuation');
      if (res.status !== 200 || res.redirected) throw new Error('list status ' + res.status);
      var ct = (res.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
      if (ct !== 'text/html') throw new Error('list content-type ' + ct);
      return res.arrayBuffer();
    }).then(function (buf) {
      // **本文が解決した時点でも** stale を弾く。離脱→復帰をまたいだ古い継続が
      // ここを通ると、新しいセッションの通信予算や候補を汚す（T03C が守っている）。
      // 必ず計上より前に置くこと。
      if (listToken !== token) throw new Error('stale list continuation');
      // 受信済みなので、上限超過で捨てる場合も必ず計上する。
      // 実測値を計上したら予約は返す（二重に数えないため。以降の判定は実測で行う）。
      state.bytes += buf.byteLength;
      listBytes += buf.byteLength;
      release();
      if (buf.byteLength > CFG.maxListBytes) throw new Error('list too large (' + buf.byteLength + ' bytes)');
      if (state.bytes > CFG.maxBytesCumulative) throw budgetExceeded('cumulative byte limit');
      var doc = new DOMParser().parseFromString(new TextDecoder('utf-8').decode(buf), 'text/html');
      var els = doc.querySelectorAll('[data-infinite-contract-kind="list"]');
      if (els.length !== 1) throw new Error('list contract count ' + els.length);
      var c = JSON.parse(els[0].getAttribute('data-infinite-contract'));
      if (useCache) {
        listContractCache[href] = c;
        listContractCacheKeys.push(href);
        while (listContractCacheKeys.length > LIST_CACHE_MAX) {
          delete listContractCache[listContractCacheKeys.shift()];
        }
      }
      return { c: c, cached: false };
    }).catch(function (e) {
      // 失敗（status / content-type / stale / abort）でも予約は必ず返す。
      // 返さないと、失敗するたびに一覧の枠が目減りして最後は掘れなくなる。
      release();
      throw e;
    });
  }

  // latch 直後に、上位タグの一覧1ページ目を **並列で** 先読みしてキャッシュに入れる。
  // グループ・帯でソースを展開すると、最初の1本に辿り着くまでに空振りのソースを
  // 何本も通る（実測: prna 記事は column グループが空なので6タグを順に引き、
  // 1本目まで 3.0 秒 / 一覧通信 10 回かかっていた）。逐次だとこの待ちが直接効くので、
  // 実操作が確認できた時点でまとめて温めておく。
  // 予算（通信回数・累積バイト）は通常経路と同じものを消費する。
  var prefetchPromise = null;     // 先読みの完了。最初の走査はこれを待つ
  var prefetchWaited = false;

  function prefetchListPages() {
    if (!listSources.length) return;
    // チェーンが契約で供給済み（next_source='list'）なら一覧は一切要らない。
    // ここを見ないと、使わない一覧を温めるだけの通信が走る
    //（実測 column407 でメタフィールド供給時: 一覧通信 5回・約100KB が丸ごと無駄だった）。
    if (state.chain.length) return;
    if (!(bandEdges.length || handleOrder.length)) return;   // 展開していないなら不要
    var seen = Object.create(null);
    var urls = [];
    for (var i = 0; i < listSources.length && urls.length < CFG.prefetchListTags; i++) {
      var u = listSources[i].url;
      if (!u || seen[u]) continue;
      seen[u] = true;
      urls.push(u);
    }
    var jobs = [];
    for (var j = 0; j < urls.length; j++) {
      (function (raw) {
        var target;
        try {
          target = new URL(raw);
          target.searchParams.set(CFG.viewParam, 'infinite-list');
          target.searchParams.delete('page');
          if (target.origin !== window.location.origin) return;
        } catch (e) { return; }
        if (listContractCache[target.href] || listInflight[target.href]) return;
        // 先読みも **通常経路と同じ admission** を通す。
        // 通信回数だけを見ていた頃は、一覧の累計バイトも全体の累積バイトも
        // 先読みには効いていなかった（2巡目 Codex 指摘）。
        if (!listAdmission('prefetch')) return;
        listNetworkFetches += 1;
        listBytesReserved += CFG.maxListBytes;
        var pfReleased = false;
        var pfRelease = function () {
          if (pfReleased) return;
          pfReleased = true;
          listBytesReserved -= CFG.maxListBytes;
          if (listBytesReserved < 0) listBytesReserved = 0;
        };
        var p = fetch(target.href, {
          method: 'GET', mode: 'same-origin', credentials: 'same-origin',
          redirect: 'error', cache: 'no-store', headers: { 'Accept': 'text/html' }
        }).then(function (res) {
          if (res.status !== 200 || res.redirected) throw new Error('status');
          var ct = (res.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
          if (ct !== 'text/html') throw new Error('content-type');
          return res.arrayBuffer();
        }).then(function (buf) {
          state.bytes += buf.byteLength;
          listBytes += buf.byteLength;
          pfRelease();
          if (buf.byteLength > CFG.maxListBytes) return;
          var doc = new DOMParser().parseFromString(new TextDecoder('utf-8').decode(buf), 'text/html');
          var els = doc.querySelectorAll('[data-infinite-contract-kind="list"]');
          if (els.length !== 1) return;
          var parsed = JSON.parse(els[0].getAttribute('data-infinite-contract'));
          listContractCache[target.href] = parsed;
          listContractCacheKeys.push(target.href);
          while (listContractCacheKeys.length > LIST_CACHE_MAX) {
            delete listContractCache[listContractCacheKeys.shift()];
          }
          return parsed;
        }).catch(function () { pfRelease(); /* 先読みの失敗は無視。通常経路が取り直す */ })
          .then(function (parsed) { pfRelease(); delete listInflight[target.href]; return parsed; });
        listInflight[target.href] = p;
        jobs.push(p);
      })(listSources[j] && listSources[j].url ? urls[j] : urls[j]);
    }
    if (jobs.length) prefetchPromise = Promise.all(jobs);
    log('一覧を先読み:', urls.length, 'タグ');
  }

  function fetchListChain(sourceUrl, pageNum) {
    if (listFetchState === 'fetching') return;
    if (state.bytes >= CFG.maxBytesCumulative) { listFetchState = 'done'; stop('byte limit'); return; }
    listFetchState = 'fetching';
    // 期待ページを閉じ込める。応答の page がこれと違えば「別のものが返った」ので捨てる。
    var wantPage = (typeof pageNum === 'number' && pageNum > 1) ? Math.floor(pageNum) : 1;
    listPendingPage = wantPage;

    // URL 生成・origin 不一致も「このソースの失敗」として通常経路に寄せる。
    // ここで return すると finalize も advance も通らず、既存候補ごと固着する。
    var u = null;
    try {
      u = new URL(sourceUrl);
      u.searchParams.set(CFG.viewParam, 'infinite-list');
      if (wantPage > 1) u.searchParams.set('page', String(wantPage));
      else u.searchParams.delete('page');
      if (u.origin !== window.location.origin) throw new Error('list origin ' + u.origin);
    } catch (e) {
      log('list url 不正:', e && e.message, sourceUrl);
      failCurrentListSource('bad url');
      return;
    }

    log('fetching list', u.href);
    // request-local な controller と token を持つ。グローバルの listAborter を timer から
    // 参照すると、古い timer が新しいリクエストを abort し得る。また pagehide / BFCache 復帰を
    // またいだ古い継続が listSourceIdx や chain を書き換えるのを token で止める。
    var ac = new AbortController();
    var token = { sourceIdx: listSourceIdx };
    listToken = token;
    listAborter = ac;
    var listTimer = setTimeout(function () { try { ac.abort(); } catch (e) {} }, CFG.listTimeoutMs);
    // 2ページ目以降＝「掘る」。本文1本ぶんの予算確保はこちらにだけ課す。
    fetchListContract(u.href, ac, token, wantPage > 1 ? 'dig' : 'first').then(function (r) {
      clearTimeout(listTimer);
      if (listToken !== token) throw new Error('stale list continuation');
      var c = r.c;

      if (c.contract_schema_version !== '2') throw new Error('list schema');
      if (c.rendered_view_discriminator !== 'blog.infinite-list') throw new Error('list discriminator');
      if (c.rendered_response_kind !== 'list_fragment') throw new Error('list kind');
      if (c.effective_template_suffix !== 'infinite-list') throw new Error('list suffix ' + c.effective_template_suffix);
      if (c.locale !== boot.locale) throw new Error('list locale');
      if (c.feature_generation !== state.generation || c.feature_enabled !== true) {
        // 無効化を観測したら list 取得も含めて即停止する（追加のリクエストを出さない）
        state.revoked = true;
        listFetchState = 'done';
        log('generation/feature の変更を検出。停止します');
        stop('revoked');
        return;
      }
      if (c.blog_handle !== boot.blog_handle) throw new Error('list blog');
      // ページ番号は重み（rank）とページ予算の土台なので、型と期待値を検証する。
      if (!Number.isSafeInteger(c.page) || c.page < 1) throw new Error('list page ' + c.page);
      if (!Number.isSafeInteger(c.pages) || c.pages < 1) throw new Error('list pages ' + c.pages);
      if (c.page !== wantPage) throw new Error('list page ' + c.page + ' != wanted ' + wantPage);
      var thisPage = c.page;
      var pages = c.pages;
      var need = state.maxAppends - state.chain.length;

      if (!c.current_tags || !c.current_tags.length) {
        // タグ絞り込みが効いていない応答は中身を信用できない。ただし別ページで
        // 集めた候補は有効なので捨てない。
        log('タグ絞り込みが効いていません（current_tags が空）:', sourceUrl);
        finalizeCurrentListSource('current_tags empty');
        return;
      }

      // 成功したページとして記録する（重複計上しない）。
      // **空振り連続数もこの「初めて成功したページか」に紐づける**。
      // 同じページの応答が二重に成功したときに streak だけ二重に進むと、
      // 実際には1ページしか空振りしていないのに見切ってしまう（2巡目 Codex 指摘）。
      var firstTimePage = listPagesSeen.indexOf(thisPage) === -1;
      if (firstTimePage) listPagesSeen.push(thisPage);
      listPendingPage = 0;

      // length を持つ非配列（文字列など）が空ページ分岐や rank 集計を通り抜けないよう、
      // 先に型を確定させる。
      if (!Array.isArray(c.items)) throw new Error('list items type');

      if (!c.items.length) {
        log('該当記事なし:', c.current_tags, 'page', thisPage);
        // 除外設定で1ページ丸ごと空になることがある。後続ページが残っていれば続ける。
        // フィルタ（帯・グループ）が効いている状態で1件も拾えないページが
        // **続いたら** 見切る（1回で見切ると死んだ帯を越えられない。上の定数参照）。
        var filterActive = bandEdges.length > 0 || handleOrder.length > 0;
        if (firstTimePage) {
          if (filterActive && collected === 0) listEmptyStreak += 1; else listEmptyStreak = 0;
        }
        var giveUp = listEmptyStreak >= LIST_EMPTY_STREAK_MAX;
        if (listCandidates.length < need && pages > thisPage && !passedBand && !giveUp &&
            canDigDeeper()) {
          listFetchState = 'idle';
          fetchListChain(sourceUrl, thisPage + 1);
          return;
        }
        finalizeCurrentListSource('empty page ' + thisPage);
        return;
      }

      // rank の有無は「応答の構造」として、候補採否フィルタより**前に**数える。
      // フィルタ後に数えると、入口記事・重複・ブロック済み・不正URL が先に落ちるので
      // 「全件 rank 欠落」でも rankMissing < items.length になり、非互換 producer を
      // 「正常に0件収集した」と誤認して次ページまで取得してしまう（10巡目の指摘）。
      var rankMissing = 0;
      for (var ri = 0; ri < c.items.length; ri++) {
        var raw = c.items[ri];
        if (!raw || !Number.isSafeInteger(raw.rank) || raw.rank < 0) rankMissing += 1;
      }
      // ここに来る時点で items は非空。1件も rank を持たない応答は producer が非互換。
      // 既存候補は catch 側の failCurrentListSource() が活かす。
      if (rankMissing === c.items.length) {
        throw new Error('list rank missing (' + rankMissing + '/' + c.items.length + ' items)');
      }
      if (rankMissing) log('rank 欠落で除外:', rankMissing, '件');

      // 候補を集める（この段階では chain に入れない）。
      // rank は契約が明示する「除外で圧縮されない生の新着順」を使う。
      // items の添字から復元してはいけない（Liquid が除外分を落とすのでずれる）。
      // このページに「対象の帯より古い記事」が含まれていたら、これ以降のページ
      // （さらに古い）に対象の帯の記事は無い。一覧は新しい順なので、そこで打ち切れる。
      // これが無いと、空振りのソースで連番ページを掘り続ける
      // （実測: prna 記事は column グループが1件も無いのに6タグ×複数ページを取得し、
      //  1本目の追記まで 3.9 秒・一覧通信 17 回かかっていた）。
      var passedBand = false;
      var srcCur = currentListSource();
      if (bandEdges.length && srcCur) {
        for (var bi = 0; bi < c.items.length; bi++) {
          var bit = c.items[bi];
          if (bit && bandOfPub(bit.pub) > srcCur.band) { passedBand = true; break; }
        }
      }

      var collected = 0;
      for (var i = 0; i < c.items.length; i++) {
        var it = c.items[i];
        if (!it || typeof it.clean_url !== 'string' || it.eligibility !== true) continue;
        var id = String(it.article_id == null ? '' : it.article_id);
        if (!id) continue;
        var itemUrl;
        try { itemUrl = new URL(it.clean_url); } catch (e) { continue; }
        if (itemUrl.origin !== window.location.origin) continue;
        if (itemUrl.search || itemUrl.hash) continue;
        if (handleIsBlocked(it.handle)) continue;                // handle ブロックリスト
        if (templateLocked && String(it.tpl || '') !== entryTemplate) continue;  // 同一テンプレのみ
        if (templateIsExcluded(it.tpl)) continue;                // 追記に向かないテンプレート
        if (candidateTooOld(it)) continue;                       // 古いニュースは出さない
        // このソースの (グループ, 帯) に属する記事だけを拾う
        if (bandEdges.length || handleOrder.length) {
          var srcNow = currentListSource();
          if (srcNow) {
            if (bandEdges.length && bandOfPub(it.pub) !== srcNow.band) continue;
            if (handleOrder.length && groupOfHandle(it.handle) !== srcNow.group) continue;
          }
        }
        if (state.candidateState[id] !== undefined) continue;    // 表示済み・失敗済みは除外
        if (id === state.entryArticleId) continue;
        var dup = false;
        for (var j = 0; j < state.chain.length; j++) if (String(state.chain[j].article_id) === id) dup = true;
        for (j = 0; j < listCandidates.length; j++) if (String(listCandidates[j].article_id) === id) dup = true;
        if (dup) continue;
        if (!Number.isSafeInteger(it.rank) || it.rank < 0) continue;   // 集計は上で済んでいる
        var rank = it.rank;
        listCandidates.push({
          article_id: it.article_id, clean_url: it.clean_url, title: it.title, handle: it.handle,
          rank: rank, weight: 1 / Math.pow(rank + 1, CFG.recencyExponent)
        });
        collected += 1;
      }
      log('list candidates', { collected: collected, total: listCandidates.length,
                               tags: c.current_tags, page: thisPage, pages: pages });

      listFetchState = 'done';

      // (1) 候補が足りないなら、次の連番ページで補充する。
      // 深さは固定ページ数ではなく **予算** で決める（canDigDeeper）。
      // フィルタ（帯・グループ）が効いている状態で1件も拾えないページが **続いたら** 見切る。
      // 1回で見切ると、使える記事の手前にある「死んだ帯」を越えられない
      //（実測 python タグ: page2〜3 がほぼ全部 curriculum で、使えるのは page4 以降だった）。
      var filterActive = bandEdges.length > 0 || handleOrder.length > 0;
      if (filterActive && collected === 0) listEmptyStreak += 1; else listEmptyStreak = 0;
      var giveUp = listEmptyStreak >= LIST_EMPTY_STREAK_MAX;
      if (listCandidates.length < need && pages > thisPage && !passedBand && !giveUp &&
          canDigDeeper()) {
        listFetchState = 'idle';
        fetchListChain(sourceUrl, thisPage + 1);
        return;
      }

      // (2) 足りていても、確率的に深いページを1回覗く。
      //     プールが大きいタグで新着50件しか露出しない偏りを避けるため。
      //     - deepPageAttempted で1ソース1回に限定する（ページ数だけを見ていると
      //       2ページ目補充の後にもう一度抽選されて二重取得になり得た）
      //     - 深い取得のあとに本文取得の予算が残ることを先に確かめる（canDigDeeper）
      if (CFG.orderMode !== 'fixed' &&
          !deepPageAttempted && pages > 1 && !passedBand && !giveUp &&
          canDigDeeper() && Math.random() < CFG.deepPageChance) {
        var dp = pickDeepPage(pages);
        if (dp) {
          deepPageAttempted = true;
          listFetchState = 'idle';
          fetchListChain(sourceUrl, dp);
          return;
        }
      }

      // (3) 選抜して chain に積む
      finalizeCurrentListSource('page ' + thisPage + '/' + pages);
    }).catch(function (err) {
      clearTimeout(listTimer);
      // 古い継続（pagehide / BFCache をまたいだもの）は state を一切触らない。
      // listFetchState も書き換えない（pagehide 側で idle へ戻してある）。
      if (listToken !== token) { log('stale list continuation ignored'); return; }
      listFetchState = 'done';
      if (teardownRequested || state.revoked) return;
      if (err && err.__budget) { log('list stopped:', err.message); stop(err.message); return; }
      log('list fetch failed:', err && err.message, sourceUrl);
      // 補充・深いページの失敗で、1ページ目までに集めた候補を捨ててはいけない。
      failCurrentListSource('fetch failed: ' + (err && err.message));
    });
  }

  function maybeLoadNext() {
    if (state.revoked) { log('skip: revoked'); return; }
    if (state.inFlight) { return; }
    if (!state.latched) { log('skip: not latched'); return; }
    if (state.appended >= state.maxAppends) return stop('append limit');
    if (state.hops >= CFG.maxHops) return stop('hop limit');
    // preflight は >=（上限ちょうどなら正のサイズの応答を収容できないので出さない）。
    // 受信後の判定は > （受信済み分を含めて上限を超えたか）。意味が違うので演算子も分ける。
    if (state.bytes >= CFG.maxBytesCumulative) return stop('byte limit');
    if (state.nodes >= CFG.maxNodesCumulative) return stop('node limit');

    // リスト指定があればそれを順に消費する。無ければ従来どおり各応答の next を辿る。
    // メタフィールド指定が無く、タグ候補があるなら、そこからチェーンを作る（方式B）
    if (!state.chain.length && !state.next && listSources.length) {
      if (listFetchState === 'idle') { tryNextListSource(); return; }
      if (listFetchState === 'fetching') return;
    }

    if (state.chain.length && state.chainIdx >= state.chain.length) {
      // リストを使い切ったら、次のタグ候補を取りに行く
      if (listSources.length && listFetchState === 'done' && advanceListSource()) {
        tryNextListSource();
        return;
      }
      return stop('end of list');
    }
    var cand = state.chain.length ? state.chain[state.chainIdx] : state.next;
    if (!cand) return stop('end of chain');

    // チェーン（方式B）では候補が最大40本あるので、**1本の問題で全体を止めない**。
    // 単一の next ポインタしか無い方式A では飛ばしようがないので従来どおり止める（F2）。
    // 進めるのは chainIdx だけなので、chain.length 回で必ず打ち切られる（無限ループ不可）。
    var id = String(cand.article_id);
    var st = state.candidateState[id] || ST.UNSEEN;
    var bad = (cand.eligibility !== true) ? 'ineligible'
            : (st === ST.APPENDED || st === ST.TERMINAL || st === ST.INELIGIBLE)
              ? ('already seen: ' + id) : null;
    if (bad) {
      if (!state.chain.length) return stop('next candidate ' + bad);
      log('候補を飛ばします:', bad);
      state.chainIdx += 1;
      updateVisibleNextLink();
      maybeLoadNext();
      return;
    }

    state.hops += 1;
    state.candidateState[id] = ST.FETCHING;
    state.inFlight = { id: id, started: Date.now(), tries: 0 };
    fetchCandidate(cand, 0);
  }

  // SSR が出した可視リンクの初期値（＝タグ一覧URL）。候補が尽きたらここへ戻す。
  var ssrNextHref = null;
  var ssrNextTitle = null;
  (function captureSsrNextLink() {
    var link = document.querySelector('[data-infinite-next-link]');
    if (!link) return;
    ssrNextHref = link.getAttribute('href');
    var t = link.querySelector('.infinite-article-next__title');
    ssrNextTitle = t ? t.textContent : null;
  })();

  function restoreVisibleNextLink() {
    var link = document.querySelector('[data-infinite-next-link]');
    if (!link) return;
    var nav = link.closest ? link.closest('.infinite-article-next') : null;
    if (ssrNextHref === null) { if (nav) nav.hidden = true; return; }
    link.setAttribute('href', ssrNextHref);
    var t = link.querySelector('.infinite-article-next__title');
    if (t && ssrNextTitle !== null) t.textContent = ssrNextTitle;
    if (nav) nav.hidden = false;
  }

  // SSR の可視リンクは入口記事基準のままなので、追記後は「次に読む候補」へ更新する。
  // JS 無効時のクロール経路は SSR の href が担うので、更新しても失うものは無い。
  // ただし **まだ読んでいない候補があるときだけ**。候補が尽きた／その候補が既に
  // 追記済みなら、SSR の初期値（タグ一覧URL）へ戻す。そうしないと
  // 「関連記事をもっと見る」が、たった今読み終わった記事を指したままになる。
  function updateVisibleNextLink() {
    var link = document.querySelector('[data-infinite-next-link]');
    if (!link) return;
    var nav = link.closest ? link.closest('.infinite-article-next') : null;
    var cand = state.chain.length ? state.chain[state.chainIdx] : state.next;
    var candState = cand ? (state.candidateState[String(cand.article_id)] || ST.UNSEEN) : null;
    var usable = cand && cand.clean_url &&
                 candState !== ST.APPENDED && candState !== ST.TERMINAL && candState !== ST.INELIGIBLE;
    if (!usable) { restoreVisibleNextLink(); return; }
    if (nav) nav.hidden = false;
    try {
      var u = new URL(cand.clean_url);
      if (u.origin !== window.location.origin) return;
      link.setAttribute('href', u.href);
    } catch (e) { return; }
    var titleEl = link.querySelector('.infinite-article-next__title');
    if (titleEl) titleEl.textContent = cand.title || '';
  }

  function stop(reason) {
    log('stop:', reason);
    if (sentinelIO) { sentinelIO.disconnect(); sentinelIO = null; }
    if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
    // 可視 next リンクは残す（S4 / F7 / F8）。ただし行き先は見直す:
    // 追記が止まった時点で未読の候補が無ければ、タグ一覧URLへ戻す。
    updateVisibleNextLink();
  }

  function transportUrlFor(cleanUrl) {
    var u = new URL(cleanUrl);
    u.search = '';
    u.hash = '';
    u.searchParams.set(CFG.viewParam, CFG.viewValue);
    return u;
  }

  // HTTP ステータスから retry 可否を判定する
  function statusIsRetryable(code) {
    return code >= 500 || code === 408 || code === 425 || code === 429;
  }

  // 例外から retry 可否を判定する。teardown 由来の中断は retry しない。
  function errorIsRetryable(err) {
    if (!err) return false;
    if (err.__retryable === true) return true;
    if (err.__terminal === true) return false;
    if (err.name === 'AbortError') return !teardownRequested;   // timeout 由来のみ retry
    if (err.name === 'TypeError') return true;                  // network error / redirect 拒否
    return false;
  }

  function fetchCandidate(cand, attempt) {
    var expected = transportUrlFor(cand.clean_url);
    if (expected.origin !== window.location.origin) { fail(cand, 'cross-origin', true); return; }

    var token = state.inFlight;                 // この試行が属するチェーン位置
    var ac = new AbortController();
    state.aborter = ac;
    // 本文を読み終わるまでタイムアウトを解除しない（ヘッダだけ来て body が止まるケース対策）
    var timer = setTimeout(function () { ac.abort(); }, CFG.fetchTimeoutMs);
    var cleared = false;
    function clearTimer() { if (!cleared) { cleared = true; clearTimeout(timer); } }

    fetch(expected.href, {
      method: 'GET',
      mode: 'same-origin',
      credentials: 'same-origin',
      redirect: 'error',       // Shopify の URL redirect は外部URLも許可するため追従自体を禁止（F5）
      cache: 'no-store',
      signal: ac.signal,
      headers: { 'Accept': 'text/html' }
    }).then(function (res) {
      if (state.inFlight !== token) throw terminal('stale');
      if (res.redirected === true) throw terminal('redirected');
      if (res.status !== 200) {
        throw (statusIsRetryable(res.status) ? retryable('status ' + res.status) : terminal('status ' + res.status));
      }
      var ct = (res.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
      if (ct !== 'text/html') throw terminal('content-type ' + ct);
      if (new URL(res.url).href !== expected.href) throw terminal('response.url mismatch');

      var len = parseInt(res.headers.get('content-length') || '0', 10);
      if (len && len > CFG.maxBytesPerResponse) throw terminal('too large (content-length)');

      // String.length は UTF-16 単位で実バイト数ではないため、必ずバイトで測る
      return res.arrayBuffer();
    }).then(function (buf) {
      clearTimer();
      if (state.inFlight !== token) throw terminal('stale');
      var bytes = buf.byteLength;
      // 実際に受信し終えているので、上限超過で捨てる場合も必ず計上する
      state.bytes += bytes;
      if (bytes > CFG.maxBytesPerResponse) throw terminal('too large (' + bytes + ' bytes)');
      if (state.bytes > CFG.maxBytesCumulative) throw terminal('cumulative byte limit');
      var text = new TextDecoder('utf-8').decode(buf);
      handleResponse(cand, expected, text, bytes, token);
    }).catch(function (err) {
      clearTimer();
      if (state.inFlight !== token) return;            // 既に別の遷移が始まっている
      if (teardownRequested) return;                   // 離脱中は何もしない
      var isRetryable = errorIsRetryable(err);
      var withinBudget = (Date.now() - token.started) < CFG.totalRetryBudgetMs;
      if (isRetryable && attempt < CFG.maxRetriesPerCandidate && withinBudget) {
        var backoff = Math.round((400 * Math.pow(2, attempt)) * (0.7 + Math.random() * 0.6));
        log('retry in', backoff, err && err.message);
        var t = setTimeout(function () {
          retryTimers.splice(retryTimers.indexOf(t), 1);
          if (state.revoked || teardownRequested || state.inFlight !== token) return;
          fetchCandidate(cand, attempt + 1);
        }, backoff);
        retryTimers.push(t);
        return;
      }
      fail(cand, (err && err.message) || 'error', !isRetryable);
    });
  }

  function terminal(msg) { var e = new Error(msg); e.__retryable = false; e.__terminal = true; return e; }
  function retryable(msg) { var e = new Error(msg); e.__retryable = true; return e; }

  // 採用できない候補を飛ばして次へ進む（チェーンは止めない）
  function skipCandidate(cand, c) {
    state.candidateState[String(cand.article_id)] = ST.INELIGIBLE;
    state.inFlight = null;
    if (state.chain.length) {
      state.chainIdx += 1;
    } else {
      state.next = (c && c.next_candidate) || null;
    }
    updateVisibleNextLink();
    maybeLoadNext();
  }

  function fail(cand, reason, isTerminal) {
    log('fail', reason);
    state.candidateState[String(cand.article_id)] = isTerminal ? ST.TERMINAL : ST.RETRYABLE;
    state.inFlight = null;
    stop('fetch failed: ' + reason);   // 自動遷移はしない（F3）。可視リンクを残す
  }

  /* ------------------------------------------------------------------ *
   * F4 / F16: 契約検証
   * ------------------------------------------------------------------ */
  function handleResponse(cand, expected, html, bytes, token) {
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch (e) { return fail(cand, 'parse error', true); }

    var cEls = doc.querySelectorAll('[data-infinite-contract-kind]');
    if (cEls.length !== 1) return fail(cand, 'contract marker count ' + cEls.length, true);

    var c;
    try { c = JSON.parse(cEls[0].getAttribute('data-infinite-contract')); }
    catch (e) { return fail(cand, 'contract parse', true); }

    // F16: generation / enabled
    if (c.feature_generation !== state.generation) { state.revoked = true; return fail(cand, 'generation changed', true); }
    if (c.feature_enabled !== true) { state.revoked = true; return fail(cand, 'feature disabled', true); }

    if (c.contract_schema_version !== '2') return fail(cand, 'schema', true);
    if (c.rendered_response_kind !== 'transport_fragment') return fail(cand, 'not a transport fragment', true);
    if (c.rendered_view_discriminator !== 'article.infinite') return fail(cand, 'view discriminator', true);
    if (c.effective_template_suffix !== 'infinite') return fail(cand, 'template suffix ' + c.effective_template_suffix, true);
    if (c.locale !== boot.locale) return fail(cand, 'locale', true);
    if (c.blog_handle !== boot.blog_handle) return fail(cand, 'blog', true);
    if (!c.initial_article) return fail(cand, 'no article in contract', true);
    if (String(c.initial_article.article_id) !== String(cand.article_id)) return fail(cand, 'article id mismatch', true);
    if (c.initial_article.clean_url !== cand.clean_url) return fail(cand, 'clean_url mismatch', true);
    // 不適格（本文が短い / 除外フラグ / handle ブロックリスト）は **その1本だけ**の問題。
    // ここで fail() を呼ぶと無限スクロール全体が止まる。チェーンには最大40本の候補が
    // 入っているので、飛ばして次へ進むのが正しい（transport 側の
    // 「loader は eligibility:false を受けると、その候補を飛ばして次へ進む」という
    // 記述とも、既存の v1 スコープ外要素の扱いともこれで一致する）。
    // 実測（2026-08-03 ユーザー報告・news905）: 2本目が ineligible で
    // `stop: fetch failed: ineligible` となり、残り39本を出せずに終わっていた。
    if (c.initial_article.eligibility !== true) {
      log('不適格なのでこの候補は飛ばします:', cand.clean_url);
      return skipCandidate(cand, c);
    }

    var payloads = doc.querySelectorAll('#infinite-payload');
    if (payloads.length !== 1) return fail(cand, 'payload count ' + payloads.length, true);
    var payload = payloads[0];
    if (!payload.querySelector('#infinite-payload-end')) return fail(cand, 'payload sentinel missing', true);

    // v1 スコープの強制: 静的な本文のみを対象にする。
    // form / script / style / custom element / inline handler / iframe srcdoc を含む記事は
    // 要素を削って意味を変えるのではなく、記事ごと採用しない（fail-closed）。
    // JSON-LD（application/ld+json）は**実行されないメタデータ**。記事本文に埋め込まれて
    // いることがあり、これを理由に記事ごと捨てると大量の記事が追記できなくなる
    // （実測: right-side テンプレートの約半数がパンくずの JSON-LD を含む）。
    // ただし**そのまま追記してはいけない**: BreadcrumbList や Article の構造化データが
    // ページ内に複数出ると、入口記事のものと競合して検索エンジンに誤解を与える。
    // したがって **取り除いてから** 追記する（本文の見た目には影響しない）。
    var ldjson = payload.querySelectorAll('script[type="application/ld+json"]');
    for (var li = 0; li < ldjson.length; li++) {
      if (ldjson[li].parentNode) ldjson[li].parentNode.removeChild(ldjson[li]);
    }
    if (ldjson.length) log('JSON-LD を除去しました:', ldjson.length, '個');

    // 残りの <script> の扱い。既定は strip（除去して記事は表示する）。
    //   reject … 1つでもあれば記事ごと捨てる（従来の挙動）
    //   strip  … 除去して本文は表示する
    // 本文に script を含む記事は少なくないうえ、内容そのものは価値が高い
    // （実測: python-*-output 系はカリキュラム記事で、YouTube 埋め込み・
    //  コードのコピーボタン・画像の拡大という自社テーマのスクリプトを持つ。
    //  同じタグ群では約2割が該当し、reject だと 40本指定でも 32本しか出なかった）。
    // 除去しても iframe 側に src が入っているので YouTube は再生できる。
    // **実行はしない**。追記した DOM で動かすと、固定IDの要素
    //（例 ta-youtube-video-player）が記事ごとに衝突して壊れる。
    if (CFG.scriptPolicy === 'strip') {
      // 指定した handle の記事だけ、**自分の近傍しか触らないスクリプト**を実行対象として残す。
      // 判定は「本文に document.currentScript を使っているか」。currentScript を起点にする
      // スクリプトは自分の位置から相対で要素を掴むので、記事が何本並んでも衝突しない。
      // 逆に getElementById など固定IDで掴むものは、2本目が1本目の要素を掴んで壊す
      //（実測: ta-youtube-video-player がこれに当たる）。
      // 外部 src のスクリプトは対象外（第三者コードを追記DOMで動かさない）。
      var execOk = false;
      var sh = '';
      if (CFG.scriptExecHandles.length) {
        // handle は候補の作り方によって欠けることがある（list から chain に積むときに
        // 落ちていた）。判定はURLの最後のパス要素からも取れるようにする。
        sh = String(cand.handle || '').toLowerCase();
        if (!sh) {
          try { sh = decodeURIComponent(new URL(cand.clean_url).pathname.split('/').pop() || '').toLowerCase(); }
          catch (e) { sh = ''; }
        }
        for (var xi = 0; xi < CFG.scriptExecHandles.length; xi++) {
          if (sh.indexOf(CFG.scriptExecHandles[xi]) !== -1) { execOk = true; break; }
        }
      }
      var scripts = payload.querySelectorAll('script');
      var kept = 0, dropped = 0;
      for (var si = scripts.length - 1; si >= 0; si--) {
        var sc = scripts[si];
        var body = sc.textContent || '';
        if (execOk && !sc.getAttribute('src') && body.indexOf('document.currentScript') !== -1) {
          sc.setAttribute('data-infinite-exec', '1');
          kept += 1;
          continue;
        }
        if (sc.parentNode) sc.parentNode.removeChild(sc);
        dropped += 1;
      }
      if (dropped || kept) log('本文の script:', dropped, '個を除去 /', kept, '個を実行対象として保持');
    }

    // 追記記事では動かない UI を消す（対応するスクリプトを実行しないため）。
    // 例: 動画の目次・「◯◯から再生」ボタンは固定IDのプレーヤー初期化に依存している。
    for (var hs = 0; hs < CFG.hideSelectors.length; hs++) {
      var gone;
      try { gone = payload.querySelectorAll(CFG.hideSelectors[hs]); } catch (e) { continue; }
      for (var gi = gone.length - 1; gi >= 0; gi--) {
        if (gone[gi].parentNode) gone[gi].parentNode.removeChild(gone[gi]);
      }
      if (gone.length) log('追記記事から除去:', CFG.hideSelectors[hs], gone.length, '個');
    }

    // <style> は設定で許可できる。転載記事のように**本文に装飾用の CSS が同梱**され、
    // それを弾くと1本も追記できなくなるケースがあるため（実測: PR TIMES 転載は全件が
    // .prt-repost-notice / .prt-summary の style を含み、topics で追記 0 本になった）。
    // 許可しても script / iframe / custom element / inline handler は従来どおり拒否する。
    // 同一内容の style は後段で重複排除するので、20本追記しても1つしか残らない。
    var scriptSel = CFG.scriptPolicy === 'strip' ? '' : 'script, ';
    var blockedSelector = CFG.allowStyle
      ? 'form, ' + scriptSel + 'iframe[srcdoc], [onclick], [onerror], [onload], [onmouseover], [onfocus]'
      : 'form, ' + scriptSel + 'style, iframe[srcdoc], [onclick], [onerror], [onload], [onmouseover], [onfocus]';
    var blocked = payload.querySelector(blockedSelector);
    if (!blocked) {
      var all = payload.getElementsByTagName('*');
      for (var bi = 0; bi < all.length; bi++) {
        var tn = all[bi].tagName;
        if (tn && tn.indexOf('-') > 0) { blocked = all[bi]; break; }   // custom element
      }
    }
    if (blocked) {
      log('v1 スコープ外の要素を含むため、この記事はスキップします:', blocked.tagName);
      return skipCandidate(cand, c);
    }

    appendArticle(cand, c, payload, token);
  }

  /* ------------------------------------------------------------------ *
   * F1 / F13 / F14: payload の無害化・名前空間化・URL 凍結 → append
   * ------------------------------------------------------------------ */
  // 同一内容の <style> を1つだけ残す。転載記事は全件が同じ装飾 CSS を同梱しているため、
  // そのままだと追記のたびに同じ style が増える。
  // 追記記事の本文に残る h1 を始末する。
  //  - PR TIMES / PR Newswire の転載は本文の中に <h1 class="prna-title"> でタイトルを持つ。
  //    テーマの prna テンプレートは見出しを別途描画しないので元記事では1回しか出ないが、
  //    転送側は全テンプレート共通で見出しを付けるため、そのままだとタイトルが2回出る。
  //  - h1 が本文に残ると、ページ内の h1 が入口記事の1つだけ、という前提（S8）も崩れる。
  // タイトルと同じ文字列なら削除、違うなら h2 へ降格する（見出し階層を保つ）。
  function headingKey(s) {
    return (s || '').replace(/\s+/g, '').replace(/[「」『』（）()【】、。,.\-–—:：]/g, '').toLowerCase();
  }
  function normalizeHeadings(root, titleEl) {
    var want = headingKey(titleEl ? titleEl.textContent : '');
    var h1s = root.getElementsByTagName('h1');
    var removed = 0, demoted = 0;
    // getElementsByTagName は live なので後ろから走査する
    for (var i = h1s.length - 1; i >= 0; i--) {
      var h = h1s[i];
      if (!h.parentNode) continue;
      if (want && headingKey(h.textContent) === want) {
        h.parentNode.removeChild(h);
        removed += 1;
        continue;
      }
      var h2 = document.createElement('h2');
      for (var ai = 0; ai < h.attributes.length; ai++) {
        h2.setAttribute(h.attributes[ai].name, h.attributes[ai].value);
      }
      while (h.firstChild) h2.appendChild(h.firstChild);
      h.parentNode.replaceChild(h2, h);
      demoted += 1;
    }
    if (removed || demoted) log('本文の h1 を整理: 重複削除', removed, '/ h2 へ降格', demoted);
  }

  // 本文内のメディアを遅延読み込みにする。
  // 転送側が loading="lazy" を付けているのはアイキャッチだけで、本文内の画像は
  // 記事HTMLのまま（実測: 追記3本の img 72 枚のうち 67 枚が loading 属性なし）。
  // 20本追記すると数百枚がまとめて取得され、モバイルの通信量と LCP に効く。
  // 既に loading が指定されているもの（作者が eager を意図した場合）は触らない。
  function lazifyMedia(root) {
    var n = 0;
    var tags = ['img', 'iframe'];
    for (var t = 0; t < tags.length; t++) {
      var els = root.getElementsByTagName(tags[t]);
      for (var i = 0; i < els.length; i++) {
        if (els[i].hasAttribute('loading')) continue;
        els[i].setAttribute('loading', 'lazy');
        if (tags[t] === 'img' && !els[i].hasAttribute('decoding')) {
          els[i].setAttribute('decoding', 'async');
        }
        n += 1;
      }
    }
    if (n) log('本文内のメディアを遅延読み込みに:', n, '件');
  }

  /* ------------------------------------------------------------------ *
   * 追記記事のカラム幅を入口記事にそろえる
   * ------------------------------------------------------------------ *
   * prna（PR Newswire 詳細）は **サイドバーの無い1カラム**テンプレートで、
   * タイトル（header）も本文も page-width のまま広がる（実測: x=108 / 1,050px。
   * 通常記事は .main_second の中なので x=153 / 585px）。
   * 追従パネルは元サイドバーの位置（x=773 / 340px）に position:fixed で出るため、
   * 1,050px の本文とパネルが 340px 分重なる（ユーザー報告のスクリーンショット）。
   * 入口の本文カラムと同じ幅・同じ左位置へ寄せて重なりを消す。
   * 追従がオフでも、記事ごとに本文幅が変わるのは読みにくいのでそろえる。
   * ------------------------------------------------------------------ */
  var BODY_SEL = '.article-template__content';
  var bodyRefEl;   // undefined=未探索 / null=見つからない
  function bodyWidthRef() {
    if (bodyRefEl === undefined) {
      bodyRefEl = null;
      // 入口が1カラムテンプレート（prna）のときは `.article-template__content`（1,050px）
      // ではなく本文カラム（`.prna-detail-main` 700px）を基準にする。
      // 前者を基準にすると追記記事が 1,050px のままになり、
      // 同じページの追従パネル（x=838）と重なる。
      var sels = [];
      if (typeof boot.main_selector === 'string' && boot.main_selector) sels.push(boot.main_selector);
      sels.push(BODY_SEL);
      for (var s = 0; s < sels.length && !bodyRefEl; s++) {
        var all;
        try { all = document.querySelectorAll(sels[s]); } catch (e) { continue; }
        for (var i = 0; i < all.length; i++) {
          // コンテナの中は追記記事なので基準にしない（入口記事だけを見る）
          if (container && container.contains(all[i])) continue;
          bodyRefEl = all[i];
          break;
        }
      }
    }
    if (!bodyRefEl) return null;
    // 画面幅で変わるので毎回測る（キャッシュするのは要素だけ）
    var r = bodyRefEl.getBoundingClientRect();
    if (r.width < 200) return null;   // 折りたたみ中などの異常値では触らない
    var c = container ? container.getBoundingClientRect() : null;
    return { w: Math.round(r.width), left: Math.round(r.left - (c ? c.left : 0)) };
  }

  // 幅をそろえた追記記事。画面幅が変わると基準も変わるのでやり直せるように控える。
  var alignWrappers = [];

  function clearAlign(el) {
    el.style.removeProperty('max-width');
    el.style.removeProperty('margin-left');
    el.style.removeProperty('margin-right');
  }

  function alignBodyWidth(wrapper) {
    if (alignWrappers.indexOf(wrapper) < 0) alignWrappers.push(wrapper);
    applyAlign(wrapper);
  }

  function sidebarBp() {
    var bp = parseInt(boot.sidebar_breakpoint, 10);
    if (!isFinite(bp) || bp < 320 || bp > 1600) bp = 990;
    return bp;
  }

  // PC 幅か（＝サイドバーが本文の横に並ぶか）。matchMedia が無い環境だけ innerWidth に落とす。
  function isWide() {
    if (window.matchMedia) return window.matchMedia('(min-width: ' + sidebarBp() + 'px)').matches;
    return window.innerWidth >= sidebarBp();
  }

  function applyAlign(wrapper) {
    var els = wrapper.querySelectorAll('.page-width');
    var i;
    // 先に前回ぶんを消して素の幅に戻す。画面幅が変わると基準も変わるため、
    // 残ったままだと「前の画面幅で測った px」で判定してしまう。
    for (i = 0; i < els.length; i++) clearAlign(els[i]);
    // SP ではサイドバーが本文の下に積まれ、追従パネルも出ない（重なりようがない）。
    // そろえる必要が無いどころか、`margin-left` がテンプレート自身の padding と
    // **二重にかかって右へずれる**（実測 375px・prna 入口:
    // 入口の段落 x=20 / 335px に対し、追記の段落が x=30 / 315px になっていた）。
    // 判定は **matchMedia** で行う。`window.innerWidth` は当てにならず、
    // 実測では1本目の追記時点で 1,041px を返していた（実際の表示は 375px）。
    // レイアウトを決めているのは CSS のメディアクエリなので、そちらに合わせる。
    if (!isWide()) return;
    var ref = bodyWidthRef();
    if (!ref) return;
    // 本文だけでなく **`page-width` を持つブロック全部** を見る。
    // prna の追記記事は直下が2つに分かれていて、本文だけ直すと
    // タイトル側が 1,050px のまま残る（ユーザー報告のスクリーンショット）:
    //   HEADER.page-width.page-width--narrow                     x=108 / 1,050px
    //   DIV.article-template__content.page-width...              x=153 /   585px
    // 通常記事の直下は `.second_colume`（page-width を持たない 960px の2カラム枠）で、
    // 幅だけで判定すると **これも縮めてしまう** ので、page-width を持つものに限る。
    var n = 0;
    for (i = 0; i < els.length; i++) {
      var el = els[i];
      // 入れ子は文書順で外側から処理される。外側を縮めると内側も収まるので
      // 内側はこの時点で基準幅以下になり、二重には当たらない。
      if (el.getBoundingClientRect().width <= ref.w + 8) continue;
      // テーマの __custom.css に `.page-width--narrow { max-width: 105rem !important }`
      // があり、素のインラインスタイルでは **負ける**（実測: inline 585px なのに
      // 実測幅は 1,050px のままだった）。important を付けて確実に勝たせる。
      el.style.setProperty('max-width', ref.w + 'px', 'important');
      el.style.marginLeft = Math.max(0, ref.left) + 'px';
      el.style.marginRight = 'auto';
      n += 1;
    }
    if (n) log('カラム幅をそろえました:', n, '要素 ->', ref.w, 'px');
  }

  // 画面幅が変わったら全部やり直す（PC ⇄ SP をまたぐと基準そのものが変わるため）。
  // **resize だけに頼らない**。このテーマは window のイベントが当てにならず
  //（scroll は 39,918px スクロールしても発火0回だった）、実測でも
  // 「1本目を追記した時点の innerWidth が 1,041px、その後 375px」という取りこぼしが出た
  // ＝ SP なのに PC 用の幅が焼き付いたままになる。
  // ブレークポイントの跨ぎは matchMedia の change で確実に拾う。
  var alignRaf = 0;
  function realignAll() {
    if (alignRaf) return;
    alignRaf = requestAnimationFrame(function () {
      alignRaf = 0;
      for (var i = 0; i < alignWrappers.length; i++) applyAlign(alignWrappers[i]);
    });
  }
  window.addEventListener('resize', realignAll, { passive: true });
  if (window.matchMedia) {
    var alignMq = window.matchMedia('(min-width: ' + sidebarBp() + 'px)');
    if (alignMq.addEventListener) alignMq.addEventListener('change', realignAll);
    else if (alignMq.addListener) alignMq.addListener(realignAll);
  }

  /* ------------------------------------------------------------------ *
   * 本文と同じ画像のサムネイルを外す
   * ------------------------------------------------------------------ *
   * prna は本文の冒頭に `.prna-thumb-wrapper` のサムネイルを持つが、
   * 本文1枚目の `figure` と **同じ URL のことがある**（実測: どちらも
   * `mmx.prnasia.com/…/image_1.jpg?id=…&p=medium600`）。
   * 追記記事では同じ画像が2枚続いて見えるので、重複しているときだけ外す。
   * **重複していなければ外さない**（サムネイルしか画像が無い記事があるため）。
   * 入口記事は対象外（サーバが返したままを読ませる）。
   * ------------------------------------------------------------------ */
  function imgKey(img) {
    var s = img.getAttribute('src') || img.getAttribute('data-src') || '';
    // サイズ違い・キャッシュバスタで別物と判定しないようにクエリと fragment を落とす
    return s ? s.split('#')[0].split('?')[0] : '';
  }

  function dropDuplicateThumb(wrapper) {
    var sel = boot.dedupe_image_selector;
    if (typeof sel !== 'string' || !sel) return;
    var holders;
    try { holders = wrapper.querySelectorAll(sel); } catch (e) { return; }
    if (!holders.length) return;
    var all = wrapper.getElementsByTagName('img');
    var count = {};
    for (var i = 0; i < all.length; i++) {
      var k = imgKey(all[i]);
      if (k) count[k] = (count[k] || 0) + 1;
    }
    var n = 0;
    for (var h = 0; h < holders.length; h++) {
      var el = holders[h];
      var img = el.tagName === 'IMG' ? el : el.getElementsByTagName('img')[0];
      if (!img) continue;
      var key = imgKey(img);
      if (!key || count[key] < 2) continue;   // 重複していないなら残す
      if (el.parentNode) { el.parentNode.removeChild(el); n += 1; }
    }
    if (n) log('本文と重複するサムネイルを外しました:', n, '件');
  }

  /* ------------------------------------------------------------------ *
   * 追記記事を途中で切って「続きを読む」にする
   * ------------------------------------------------------------------ *
   * 全文を DOM に入れると重い（実測: カリキュラム記事1本で 20,479px / 2,369ノード。
   * 40本で 45,284ノードになり、上限 40,000 を超えた）。
   * **CSS で隠すだけでは DOM は1ノードも減らない**ので、切った先は
   * DocumentFragment に退避し、クリックされたときだけ DOM へ戻す。
   * 入口記事は対象外（サーバが返した本文をそのまま読ませる）。
   * ------------------------------------------------------------------ */
  function truncateArticle(wrapper, articleUrl) {
    if (CFG.excerptMode !== 'headings' && CFG.excerptMode !== 'height') return 0;
    var body = wrapper.querySelector('.article-template__content');
    if (!body) return 0;

    // 本文が入れ子の div に包まれているテンプレートがある。
    // 実測（prna-list3878 / main-prna-article）:
    //   .article-template__content            子1
    //     └ .prna-detail-container            子2
    //         ├ .prna-detail-main             子1  本文 4,459字
    //         │   └ .prna-article-container   子5
    //         │       └ .prna-article-body    ← 本文はここ
    //         └ .prna-detail-sidebar          子2  528字（サイドバーが本文の中にある）
    //
    // 直下の子をそのまま切る単位にすると、`.prna-detail-container` の2子＝
    // 「本文まるごと」と「サイドバー」になり、**サイドバーを1つ消して終わり**になる。
    // 本文は丸ごと残り、CTA だけが付く（ユーザー報告の症状）。
    //
    // そこで「本文の大半を1つの子が抱えている限り、その子へ降りる」ことにする。
    // 段落が兄弟に分かれた時点で止まる＝そこが切る単位。
    // 通常の記事は最初から段落が並んでいるので、この while は1回も回らない。
    //
    // ただし **降りすぎると切る単位が無くなる**。school-detail は入れ子が深く、
    // 6段降りた先が子1つの要素になって `kids.length < 2` で CTA が出なくなっていた
    // （実測 school-detail6850: content(子4) → c-section(1) → c-section__inner(4)
    //   → sr__general_area(1) → sr__general__inner_col1(2) → p-general__boxes(3)
    //   → sr__general_area(子1) で打ち止め。11本すべて CTA 無しだった）。
    // 降りた先が切れない形だったら **元の階層に戻す**。
    // 途中の階層に戻すと、切った下にまだ別セクションが残って CTA の後ろに本文が続く。
    var origin = body;
    var guard = 0;
    while (guard < 6 && body.children.length > 0 && body.children.length <= 6) {
      var total = (body.textContent || '').length;
      if (total < 200) break;
      var best = null, bestLen = 0;
      for (var ci = 0; ci < body.children.length; ci++) {
        var len = (body.children[ci].textContent || '').length;
        if (len > bestLen) { bestLen = len; best = body.children[ci]; }
      }
      // 1つの子が本文の大半（70%以上）を抱えているなら、それは「入れ物」。中へ降りる。
      if (!best || bestLen < total * 0.7 || !best.children.length) break;
      body = best;
      guard += 1;
    }
    if (body.children.length < 2 && origin.children.length >= 2) body = origin;

    var kids = body.children;                 // live HTMLCollection
    if (kids.length < 2) return 0;

    // 見出しの数と高さの **どちらか早い方** で切る。
    //   headings … 見出し優先。見出しが足りない記事は高さで切る（上限として効く）
    //   height   … 高さだけ
    // 見出しだけだと、見出しが2つしかない記事が丸ごと残る
    //（実測: PR TIMES 12本中3本が該当。うち1本は 200ブロック・9,266字で全文表示だった）。
    var cut = -1;
    var useHeadings = CFG.excerptMode === 'headings';
    var base = body.getBoundingClientRect().top;
    var seen = 0;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (useHeadings) {
        var isH = /^H[2-6]$/.test(el.tagName) || !!el.querySelector('h2,h3,h4,h5,h6');
        if (isH) {
          seen += 1;
          if (seen > CFG.excerptHeadings) { cut = i; break; }
        }
      }
      if (el.getBoundingClientRect().bottom - base > CFG.excerptHeight) { cut = i; break; }
    }
    // 先頭で切ると本文が丸ごと消える。最低1ブロックは残す。
    if (cut === 0) cut = 1;
    if (cut < 1 || cut >= kids.length) return 0;

    // **フェード帯に表を掛けない**（2026-08-31 ユーザー報告）。
    // フェードは半透明の帯を本文の上に重ねるだけなので、下が普通のテキストなら
    // 背景色へ溶けるが、<table> は溶けない:
    //   ・thead.thead_sticky / th.th_sticky が濃紺の背景を持つので色が抜けず、
    //     ヘッダー行だけ素の濃さで残る
    //   ・div.table_area は横スクロールのコンテナで、スクロールバーが帯の上に出る
    // 結果、「表が途中で切れた」ように見える（背景が白のうちは白地に白で
    // 目立たなかったが、薄グレーにしたことで露出した）。
    // 帯に掛かるブロックに表があれば、その表ごと切り落として、
    // フェードが本文テキストの上だけに乗るようにする。
    var fadePx = parseFloat(getComputedStyle(document.documentElement)
      .getPropertyValue('--infinite-more-fade')) || 140;
    var tguard = 0;
    while (cut > 1 && tguard++ < 60) {
      var fadeTop = kids[cut - 1].getBoundingClientRect().bottom - fadePx;
      var hitAt = -1;
      for (var j = cut - 1; j >= 0; j--) {
        var rj = kids[j].getBoundingClientRect();
        if (rj.bottom <= fadeTop) break;      // 帯より上のブロックは無関係
        if (kids[j].tagName === 'TABLE' || kids[j].querySelector('table')) {
          hitAt = j; break;
        }
      }
      if (hitAt < 1) break;                   // 表が無い／先頭ブロックなら諦める
      cut = hitAt;                            // その表ごと切る
    }

    // 切り取った先の扱いは excerptAction で決める。
    //   'link'   … 記事ページへのリンクにする。切った本文は **捨てる**（既定）
    //   'expand' … その場で戻す。切った本文を DocumentFragment に保持する（従来）
    // link のときに frag を持ち続けると、CTA が生きている間ずっと
    // 切り取ったノードがクロージャに掴まれて GC されない（40本ぶん残る）。
    var toLink = CFG.excerptAction === 'link';
    var frag = toLink ? null : document.createDocumentFragment();
    var removed = 0;
    while (kids.length > cut) {
      // kids は live HTMLCollection。**必ず body から外す**こと。
      // 外さないと kids.length が減らず無限ループになり、メインスレッドが固まる
      //（2026-08-04 Codex レビュー指摘。frag.appendChild を消すだけだとこれを踏む）。
      var node = kids[cut];
      removed += 1 + node.getElementsByTagName('*').length;
      if (frag) frag.appendChild(node);
      else body.removeChild(node);
    }
    if (!removed) return 0;

    var more = document.createElement('div');
    more.className = 'infinite-article-more';
    var cta;
    if (toLink) {
      // 遷移するので <button> ではなく <a href>。
      // Tab/Enter・中クリック・別タブで開く・リンクをコピー が標準どおり効く。
      // role="button" や Space のキーハンドラは足さない（リンクの操作体系を壊すため）。
      cta = document.createElement('a');
      cta.href = articleUrl || '';
      // 追記記事が並ぶと CTA の読み上げ名がすべて「続きを読む」になり、
      // どの記事へのリンクか分からない（WCAG 2.4.4）。表示は変えずに名前へ記事名を足す。
      // 表示ラベルを名前の先頭に含めるのは音声操作との整合のため（WCAG 2.5.3）。
      var titleEl = wrapper.querySelector('[data-infinite-article-title]');
      var t = titleEl ? (titleEl.textContent || '').trim() : '';
      if (t) cta.setAttribute('aria-label', CFG.excerptLabel + '：' + t);
    } else {
      cta = document.createElement('button');
      cta.type = 'button';
      cta.addEventListener('click', function () {
        body.appendChild(frag);
        if (more.parentNode) more.parentNode.removeChild(more);
        state.nodes += removed;
        rehighlightCode(body);
        lazifyMedia(body);
        activateScripts(body);
        log('続きを読む: 展開しました', removed, 'ノード');
      });
    }
    cta.className = 'infinite-article-more__button';
    cta.textContent = CFG.excerptLabel;
    more.appendChild(cta);
    body.appendChild(more);
    return removed;
  }

  // 保持したスクリプトを **その場で** 実行可能な要素に差し替える。
  // DOMParser 由来の script は挿入しても実行されないため、同じ位置に作り直す。
  // 位置を変えてはいけない: document.currentScript を起点に
  // previousElementSibling / parentNode で要素を掴むので、位置がずれると別物を触る。
  function activateScripts(root) {
    var list = root.querySelectorAll('script[data-infinite-exec="1"]');
    var n = 0;
    for (var i = 0; i < list.length; i++) {
      var old = list[i];
      if (!old.parentNode) continue;
      var fresh = document.createElement('script');
      fresh.textContent = old.textContent || '';
      try { old.parentNode.replaceChild(fresh, old); n += 1; } catch (e) {}
    }
    if (n) log('本文のスクリプトを実行しました:', n, '個');
  }

  var seenStyles = {};
  function dedupeStyles(root) {
    var styles = root.getElementsByTagName('style');
    for (var i = styles.length - 1; i >= 0; i--) {
      var key = (styles[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (!key || seenStyles[key]) {
        if (styles[i].parentNode) styles[i].parentNode.removeChild(styles[i]);
      } else {
        seenStyles[key] = true;
      }
    }
  }

  // 追記した本文のコードブロックを、ページに既にあるハイライタで色付けし直す。
  //
  // なぜ必要か: 多くのハイライタは読み込み時に1回だけ走るので、後から挿入した
  // コードは未処理のまま残る。このテーマ（Google Code Prettify）は
  // `pre` の背景が黒・既定の文字色が黒に近いため、未処理だと**真っ黒**になって読めない
  // （実測: background rgb(0,0,0) / color rgba(18,18,18,0.75)）。
  //
  // transport 由来の script は実行しない方針（F1）なので、**ページに既にある**
  // ハイライタの API だけを呼ぶ。存在しなければ何もしない。
  function rehighlightCode(root) {
    try {
      // Google Code Prettify: 未処理（prettyprinted が付いていない）要素だけを処理する
      if (window.PR && typeof window.PR.prettyPrint === 'function') {
        if (root.querySelector('pre.prettyprint:not(.prettyprinted), code.prettyprint:not(.prettyprinted)')) {
          window.PR.prettyPrint();
        }
      }
      // Prism / highlight.js（このテーマには無いが、他テーマへの流用を想定）
      if (window.Prism && typeof window.Prism.highlightAllUnder === 'function') {
        window.Prism.highlightAllUnder(root);
      } else if (window.hljs && typeof window.hljs.highlightElement === 'function') {
        var blocks = root.querySelectorAll('pre code:not(.hljs)');
        for (var i = 0; i < blocks.length; i++) window.hljs.highlightElement(blocks[i]);
      }
    } catch (e) {
      log('コードハイライトの再実行に失敗:', e && e.message);
    }
  }

  function appendArticle(cand, c, payload, token) {
    var base = c.initial_article.source_base_url || c.initial_article.clean_url;

    // 1) payload 外・危険要素を除去（F1 / S8）
    //    script は **実行対象として印を付けたものだけ残す**。印は前段の
    //    scriptPolicy 判定で付く（handle パターンに当たり、かつ本文が
    //    document.currentScript を使うもの＝自分の近傍しか触らないものだけ）。
    //    ここで無条件に消していたため、印を付けても必ず落ちていた。
    ['script', 'base', 'link', 'meta', 'template'].forEach(function (sel) {
      var nodes = payload.querySelectorAll(sel);
      for (var i = nodes.length - 1; i >= 0; i--) {
        if (sel === 'script' && nodes[i].getAttribute('data-infinite-exec') === '1') continue;
        if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
      }
    });
    var sen = payload.querySelector('#infinite-payload-end');
    if (sen) sen.parentNode.removeChild(sen);

    // 2) F13: ID の名前空間化（本文の list1 / cta-0001 が記事間で衝突するため必須）
    var prefix = CFG.idPrefix + String(cand.article_id) + '-';
    var idMap = Object.create(null);
    var withId = payload.querySelectorAll('[id]');
    for (var i = 0; i < withId.length; i++) {
      var oldId = withId[i].id;
      if (!oldId) continue;
      idMap[oldId] = prefix + oldId;
      withId[i].id = idMap[oldId];
    }
    var idrefAttrs = ['for', 'aria-labelledby', 'aria-describedby', 'aria-details', 'aria-errormessage',
      'aria-owns', 'aria-activedescendant', 'aria-flowto', 'aria-controls', 'itemref',
      'popovertarget', 'commandfor'];
    idrefAttrs.forEach(function (attr) {
      var els = payload.querySelectorAll('[' + attr + ']');
      for (var j = 0; j < els.length; j++) {
        var mapped = els[j].getAttribute(attr).split(/\s+/).map(function (t) { return idMap[t] || t; }).join(' ');
        els[j].setAttribute(attr, mapped);
      }
    });
    // SVG の内部参照（local ID 参照は絶対化しない / F14 の分類表）
    var svgRefs = payload.querySelectorAll('[href^="#"], [xlink\\:href^="#"]');
    for (var s = 0; s < svgRefs.length; s++) {
      var el = svgRefs[s];
      if (el.tagName && el.tagName.toLowerCase() === 'a') continue; // アンカーは後段で処理
      ['href', 'xlink:href'].forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && v.charAt(0) === '#') {
          var t = v.slice(1);
          if (idMap[t]) el.setAttribute(attr, '#' + idMap[t]);
        }
      });
    }
    // SVG の paint 参照（fill / stroke / clip-path / mask / filter / marker-*）
    var paintAttrs = ['fill', 'stroke', 'clip-path', 'mask', 'filter',
                      'marker-start', 'marker-mid', 'marker-end'];
    paintAttrs.forEach(function (attr) {
      var els = payload.querySelectorAll('[' + attr + ']');
      for (var pi = 0; pi < els.length; pi++) {
        var v = els[pi].getAttribute(attr);
        if (!v || v.indexOf('url(') === -1) continue;
        els[pi].setAttribute(attr, v.replace(/url\(\s*['"]?#([^)'"]+)['"]?\s*\)/g, function (m, g) {
          return 'url(#' + (idMap[g] || g) + ')';
        }));
      }
    });

    // inline style の url(#id)
    var styled = payload.querySelectorAll('[style*="url(#"]');
    for (var t2 = 0; t2 < styled.length; t2++) {
      styled[t2].setAttribute('style', styled[t2].getAttribute('style').replace(/url\(#([^)]+)\)/g, function (m, g) {
        return 'url(#' + (idMap[g] || g) + ')';
      }));
    }

    // 3) F14: 本文内アンカー（#...）は URL を変えずに合成 DOM 内をスクロールする
    var anchors = payload.querySelectorAll('a[href^="#"]');
    for (var a2 = 0; a2 < anchors.length; a2++) {
      var an = anchors[a2];
      var rawTarget = an.getAttribute('href').slice(1);
      var target = rawTarget;
      try { target = decodeURIComponent(rawTarget); } catch (e) { target = rawTarget; }
      var mappedTarget = idMap[target] || idMap[rawTarget] || target;
      an.setAttribute('href', '#' + mappedTarget);
      an.setAttribute('data-infinite-local-anchor', mappedTarget);
    }

    // 4) F14: 相対URLを記事B基準で絶対化（navigation / subresource）
    var urlAttrs = ['href', 'src', 'poster', 'data', 'cite'];
    urlAttrs.forEach(function (attr) {
      var els = payload.querySelectorAll('[' + attr + ']');
      for (var k2 = 0; k2 < els.length; k2++) {
        var v2 = els[k2].getAttribute(attr);
        if (!v2 || v2.charAt(0) === '#') continue;
        if (/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(v2) || v2.indexOf('//') === 0) continue; // 既に絶対
        try { els[k2].setAttribute(attr, new URL(v2, base).href); } catch (e) {}
      }
    });
    var srcsets = payload.querySelectorAll('[srcset]');
    for (var q = 0; q < srcsets.length; q++) {
      var rawSrcset = srcsets[q].getAttribute('srcset') || '';
      // data URL はそれ自体にカンマを含むため、単純分割すると壊れる。
      // その場合は書き換えを行わない（Shopify の RTE は絶対CDN URLを出すので実害は無い）。
      if (rawSrcset.indexOf('data:') !== -1) continue;
      var parts = rawSrcset.split(',').map(function (p) {
        var seg = p.trim().split(/\s+/);
        if (!seg[0]) return p;
        if (!/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(seg[0]) && seg[0].indexOf('//') !== 0) {
          try { seg[0] = new URL(seg[0], base).href; } catch (e) {}
        }
        return seg.join(' ');
      });
      srcsets[q].setAttribute('srcset', parts.join(', '));
    }

    // 5) 性能（F12）と CLS 対策
    //   対象ストアの記事本文には width/height を持たない画像しかない（実測 20/20）。
    //   寸法が未確定のまま loading="lazy" を付けると、遅れて実寸へ展開したときに
    //   後続要素を押し下げる（post-load CLS）。そこで自前の lazy 付与はやめ、
    //   挿入前に自然寸法を解決して属性へ書き込む。テーマに
    //   `.rte img { height: auto; max-width: 100% }` があるため、width/height 属性だけで
    //   アスペクト比が確定し、画像の読み込み前に領域が確保される。
    var imgs = payload.querySelectorAll('img');
    for (var m2 = 0; m2 < imgs.length; m2++) {
      if (!imgs[m2].getAttribute('decoding')) imgs[m2].setAttribute('decoding', 'async');
    }
    var frames = payload.querySelectorAll('iframe');
    for (var n2 = 0; n2 < frames.length; n2++) {
      if (!frames[n2].getAttribute('loading')) frames[n2].setAttribute('loading', 'lazy');
    }

    // 非同期の継続はすべて token（この試行の inFlight）を検証する。
    // pagehide → BFCache 復帰で古い継続が動くと二重 append になるため。
    resolveImageDimensions(payload, function () {
      if (!continuationIsValid(token)) return;
      whenInsertionPointIsSafe(function () {
        if (!continuationIsValid(token)) return;
        // 別 task から呼ばれるため、例外が fetch の .catch() へ戻らない。
        // ここで捕まえないと inFlight を握ったまま loader が止まる。
        try {
          finishAppend(cand, c, payload, prefix, token);
        } catch (e) {
          log('append failed', e && e.message);
          state.inFlight = null;
          stop('append error');
        }
      });
    });
  }

  // 追記済みの wrapper が既に DOM にあるか。ID をセレクタへ連結しない（値の直接比較）。
  function wrapperExists(candId) {
    var els = container.children;
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute && els[i].getAttribute('data-infinite-article-id') === candId) return true;
    }
    return false;
  }

  // 非同期の継続を進めてよいか。停止中・離脱中・別の試行に切り替わっていれば進めない。
  function continuationIsValid(token) {
    if (state.revoked || teardownRequested) return false;
    if (token && state.inFlight !== token) { log('stale continuation'); return false; }
    return true;
  }

  // 追記前に画像の自然寸法を解決し、width / height 属性として書き込む（CLS 対策）。
  // 解決できなかった画像には何も書かない（寸法を捏造しない）。上限時間で必ず打ち切る。
  //
  // 画像の形によって予約の仕方を変える（誤った表示幅を書かないため）。
  //   attrs : width/height が両方無く、srcset も <picture> も無い単純な img
  //           → width/height 属性を書く（表示寸法とアスペクト比の両方の hint になる）
  //   ratio : srcset を使う img / 片側だけ寸法がある img
  //           → 表示幅は変えず `aspect-ratio: auto w / h` だけを予約する。
  //             `auto` を付けるのが重要で、これが無いと実画像が読めた後も指定比率が
  //             preferred ratio として使われ続け、比率が違えば恒久的に歪む。
  //             srcset の候補は幅が違っても比率は同じであるのが通常なので、先頭候補で予約できる。
  //   片側だけ寸法がある場合は、その値を保ったまま不足側を比率から計算する。
  //   <picture> は art direction で候補ごとに比率が変わり得るうえ、実際に選ばれる <source> を
  //   JS 側で再現できないため対象外にする（誤った比率を強制するより無予約の方が安全）。
  //
  //   属性は3つの状態に分ける。「予約に使えない」と「上書きしてよい」は別物なので、
  //   同じ値で表現してはいけない。
  //     missing  : 属性が無い、または空文字（テンプレートが未設定値を width="" で出すことがある）
  //     positive : ASCII の正の整数。予約に使える
  //     other    : 上記以外の明示値（0 / 50% / 不正値）。**触らない**
  //   HTML では width="0" height="0" は「この要素はユーザー向けではない」ことを表す正当な指定
  //   （計測用ピクセル等）なので、自然寸法で上書きすると非表示だった画像を表示させてしまう。
  //   また JS の parseFloat は HTML の寸法属性パーサーではない（"50%" → 50、"1e309" → Infinity）
  //   ため、ASCII 整数として厳密に判定する。
  function dimensionState(el, name) {
    if (!el.hasAttribute(name)) return { kind: 'missing', value: 0 };
    var raw = el.getAttribute(name) || '';
    // 空白だけの値はテンプレートの未設定値とみなす
    if (raw.trim() === '') return { kind: 'missing', value: 0 };
    // 数値判定は trim 後ではなく **生の属性値** に対して行う。
    // JS の trim() は NBSP や BOM など非ASCII空白まで落とすため、trim 後に判定すると
    // width="&nbsp;640" のような不正値が positive に化けて「触らない」ガードを迂回する。
    if (!/^[0-9]+$/.test(raw)) return { kind: 'other', value: 0 };
    var v = parseInt(raw, 10);
    if (!isFinite(v) || v <= 0) return { kind: 'other', value: 0 };   // "0" もここ
    return { kind: 'positive', value: v };
  }

  function resolveImageDimensions(payload, done) {
    var list = payload.querySelectorAll('img');
    var pending = [];
    for (var i = 0; i < list.length; i++) {
      var im = list[i];
      var stW = dimensionState(im, 'width');
      var stH = dimensionState(im, 'height');
      // 明示された別の値（0 や 割合）があるものは一切触らない
      if (stW.kind === 'other' || stH.kind === 'other') continue;
      if (stW.kind === 'positive' && stH.kind === 'positive') continue;   // 既に有効な比率がある
      var attrW = stW.kind === 'positive' ? stW.value : 0;
      var attrH = stH.kind === 'positive' ? stH.value : 0;
      // CSS 宣言として確認する（生文字列の部分一致だと --aspect-ratio 等を誤検出する）
      if (im.style && im.style.getPropertyValue('aspect-ratio') !== '') continue;

      var parentTag = im.parentNode && im.parentNode.tagName;
      if (parentTag && String(parentTag).toLowerCase() === 'picture') continue;
      var hasSrcset = (im.getAttribute('srcset') || '').trim() !== '';

      // probe に使う URL。srcset しか無い場合は先頭候補（比率の取得だけに使う）
      var src = (im.getAttribute('src') || '').trim();
      if (!src && hasSrcset) {
        src = (im.getAttribute('srcset') || '').split(',')[0].trim().split(/\s+/)[0] || '';
      }
      if (!src) continue;
      if (/^data:/i.test(src)) continue;                             // data URL は probe しない

      var mode = (!attrW && !attrH && !hasSrcset) ? 'attrs' : 'ratio';
      pending.push({ el: im, src: src, probe: null, mode: mode, attrW: attrW, attrH: attrH });
    }
    if (!pending.length) { done(); return; }

    var left = pending.length;
    var settled = false;
    var timer = setTimeout(finish, CFG.imgDimTimeoutMs);
    var cleanup = registerCleanup(function () {
      clearTimeout(timer);
      settled = true;
      release();
    });

    // 打ち切り後も probe が読み込みを続けないよう、ハンドラと src を外す
    function release() {
      for (var r = 0; r < pending.length; r++) {
        var pr = pending[r].probe;
        if (!pr) continue;
        pr.onload = null;
        pr.onerror = null;
        try { pr.src = ''; } catch (e) {}
      }
    }

    // 属性の書き込みは finish の時点でまとめて行う。
    // timeout 満了時でも、その時点で自然寸法が判明している probe の値は採用する
    // （load 完了前でもヘッダだけで寸法が確定していることがあるため）。
    function commitResolved() {
      for (var k = 0; k < pending.length; k++) {
        var p = pending[k];
        if (!p.probe) continue;
        var nw = p.probe.naturalWidth, nh = p.probe.naturalHeight;
        if (!(nw > 0 && nh > 0)) continue;
        if (p.mode === 'attrs') {
          p.el.setAttribute('width', String(nw));
          p.el.setAttribute('height', String(nh));
          continue;
        }
        // 片側だけ指定されている場合は、その値を保ったまま不足側を比率から計算する。
        // 丸めて 0 になる／有限でない場合は属性を捏造せず、下の比率予約へ落とす。
        var derived = 0;
        if (p.attrW && !p.attrH) {
          derived = Math.round(p.attrW * nh / nw);
          if (isFinite(derived) && derived > 0) { p.el.setAttribute('height', String(derived)); continue; }
        }
        if (p.attrH && !p.attrW) {
          derived = Math.round(p.attrH * nw / nh);
          if (isFinite(derived) && derived > 0) { p.el.setAttribute('width', String(derived)); continue; }
        }
        // srcset: 表示幅は変えず、比率だけを予約する。
        // `auto` を必ず付ける = 自然比率が判明したらそちらへ切り替わる（HTML の width/height 属性と
        // 同じなのは「切り替わり方」だけで、寸法プロパティへのマップは無い。したがって
        // max-width で縮む場合、読み込み前後の高さが完全に一致する保証まではない）。
        if (p.el.style) p.el.style.setProperty('aspect-ratio', 'auto ' + nw + ' / ' + nh);
      }
    }

    function finish() {
      if (settled) return;
      settled = true;
      commitResolved();
      runCleanup(cleanup);   // clearTimeout と release はこの中で行う（二重実行しない）
      done();
    }

    pending.forEach(function (p) {
      var probe = new Image();
      p.probe = probe;
      // 元画像の取得条件を引き継ぐ（referrer / CORS の挙動を変えないため）
      var rp = p.el.getAttribute('referrerpolicy');
      if (rp) probe.referrerPolicy = rp;
      var co = p.el.getAttribute('crossorigin');
      if (co !== null) probe.crossOrigin = co;
      function onSettle() {
        left -= 1;
        if (left <= 0) finish();
      }
      probe.onload = onSettle;
      probe.onerror = onSettle;
      try { probe.src = p.src; } catch (e) { onSettle(); }
    });
  }

  // 挿入点（container の下端）が画面内にあると、その下の可視「次の記事」リンクが
  // 押し下げられてレイアウトシフトになる。画面外へ出るまで待つ。
  // これ以上スクロールできない位置では待っても解消しないので、上限時間で打ち切る。
  function whenInsertionPointIsSafe(run) {
    function safe() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = container.getBoundingClientRect();
      if (r.bottom >= vh) return true;                   // 挿入点が画面下端より下
      var doc = document.documentElement;
      var y = window.scrollY || window.pageYOffset || 0;
      var maxScroll = Math.max(0, (doc.scrollHeight || 0) - vh);
      return (maxScroll - y) <= 2;                       // これ以上スクロールできない
    }
    if (safe()) { run(); return; }
    var started = Date.now();
    var iv = setInterval(check, 200);
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    var cleanup = registerCleanup(function () {
      clearInterval(iv);
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    });
    var fired = false;
    function check() {
      if (fired) return;
      if (state.revoked || teardownRequested) { fired = true; runCleanup(cleanup); state.inFlight = null; return; }
      // insertDeferMaxMs は目安。タイマーはメインスレッド負荷やバックグラウンド化で遅れる。
      if (safe() || (Date.now() - started) >= CFG.insertDeferMaxMs) {
        fired = true;
        runCleanup(cleanup);
        run();
      }
    }
  }

  function finishAppend(cand, c, payload, prefix, token) {
    if (!continuationIsValid(token)) return;

    // one-shot: 同じ候補を二度 append しない（非同期継続が復帰後に重複実行された場合の保険）。
    // 発動したら inFlight を必ず解放してから停止する。握ったままだと maybeLoadNext() が
    // 以後すべて早期 return し、loader がデッドロックする。
    var candId = String(cand.article_id);
    if (state.candidateState[candId] === ST.APPENDED || wrapperExists(candId)) {
      log('duplicate append detected', candId);
      if (state.inFlight === token) state.inFlight = null;
      return stop('duplicate append state');
    }

    // 6) 予算チェック → append
    var nodeCount = payload.querySelectorAll('*').length;
    if (state.nodes + nodeCount > CFG.maxNodesCumulative) { state.inFlight = null; return stop('node budget'); }

    var wrapper = document.createElement('article');
    wrapper.className = 'article-template infinite-article';
    wrapper.setAttribute('data-infinite-article-id', String(cand.article_id));
    var titleEl = payload.querySelector('[data-infinite-article-title]');
    if (titleEl && titleEl.id) { /* 既に名前空間化済み */ } else if (titleEl) { titleEl.id = prefix + 'title'; }
    if (titleEl) wrapper.setAttribute('aria-labelledby', titleEl.id || (prefix + 'title'));
    while (payload.firstChild) wrapper.appendChild(payload.firstChild);
    normalizeHeadings(wrapper, titleEl);
    lazifyMedia(wrapper);
    dedupeStyles(wrapper);
    // 見出しで切る場合はレイアウトが要らないので **挿入前** に切る。
    // 挿入してから縮めると、その記事が画面に近いときに下の要素が跳ね上がって
    // CLS になる。高さで切る場合だけは測定のために挿入後に行う。
    container.appendChild(wrapper);
    // 高さで切る前に幅をそろえ、重複サムネイルを外す。
    // narrow にすると本文が縦に伸び、画像を外すと縮むので、
    // 逆順だと切る位置が実際の見え方とずれる。
    dropDuplicateThumb(wrapper);
    alignBodyWidth(wrapper);
    rehighlightCode(wrapper);
    activateScripts(wrapper);
    // 高さを測る必要があるので挿入後に切る。追記位置は画面の 3,500px 下なので、
    // ここで縮んでも画面内の要素は動かない（実測でも追記由来の CLS は出ていない）。
    truncateArticle(wrapper, cand.clean_url);

    state.appended += 1;
    state.seq += 1;
    // 差分ではなく **実際に DOM へ入った数** を数える。
    // 差分だと、コード整形（prettify）が span を増やしたあとに切り詰めた場合に
    // 引く数が元の数を超えて負になり、DOM 上限のチェックが効かなくなる
    //（実測: 40本追記で state.nodes が -16,479 になっていた）。
    state.nodes += wrapper.getElementsByTagName('*').length;
    state.candidateState[String(cand.article_id)] = ST.APPENDED;
    state.inFlight = null;

    var entry = {
      id: String(cand.article_id),
      cleanUrl: c.initial_article.clean_url,
      title: c.initial_article.document_title,
      el: wrapper,
      seq: state.seq
    };
    state.articles.push(entry);
    if (state.pvState[entry.id] === undefined) state.pvState[entry.id] = PV.NOT_ATTEMPTED;
    if (primaryIO) primaryIO.observe(wrapper);

    // 次候補: リスト指定モードでは入口記事のリストを進めるだけ（応答の next は使わない）。
    // それ以外は「検証済みの応答」から受け取る（F4: 期待値は直前の契約由来）。
    if (state.chain.length) {
      state.chainIdx += 1;
      if (state.chainIdx >= state.chain.length) state.next = null;
    } else {
      state.next = c.next_candidate || null;
    }
    log('appended', entry.id, 'next=', state.next && state.next.article_id);

    lastAppendScrollY = window.scrollY || window.pageYOffset || 0;
    updateVisibleNextLink();

    if (state.appended >= state.maxAppends) {
      stop('append limit reached');
    } else {
      // 追記でレイアウトが変わるので主可視判定だけ回す（次の読み込みはスクロールを待つ）
      checkPrimaryByRect();
    }
  }

  /* ------------------------------------------------------------------ *
   * 本文内アンカー: preventDefault + scrollIntoView（URL を変えない / F14）
   * ------------------------------------------------------------------ */
  container.addEventListener('click', function (ev) {
    var a = ev.target && ev.target.closest ? ev.target.closest('a[data-infinite-local-anchor]') : null;
    if (!a) return;
    var id = a.getAttribute('data-infinite-local-anchor');
    var target = id ? document.getElementById(id) : null;
    if (!target) return;
    ev.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ------------------------------------------------------------------ *
   * teardown（F3）
   * ------------------------------------------------------------------ */
  window.addEventListener('pagehide', function () {
    teardownRequested = true;
    if (state.aborter) { try { state.aborter.abort(); } catch (e) {} }
    if (listAborter) { try { listAborter.abort(); } catch (e) {} }
    for (var i = 0; i < retryTimers.length; i++) clearTimeout(retryTimers[i]);
    retryTimers.length = 0;
    cleanupAllAsync();          // 画像 probe / 挿入位置待ちの継続を確実に止める
    // 実行中だった list は復帰後に同じ source から再試行できるよう idle へ戻す。
    // token を捨てるので、古い継続が復帰しても state を触れない。
    listToken = null;
    if (listFetchState === 'fetching') listFetchState = 'idle';
    // 中断した候補の traversal を巻き戻す。巻き戻さないと、BFCache 復帰のたびに
    // 同じ候補で hop を消費し、candidateState も FETCHING のまま固着する。
    if (state.inFlight && state.inFlight.id) {
      // hop を消費したのは FETCHING にした本人だけ。同じ条件の内側で戻す。
      if (state.candidateState[state.inFlight.id] === ST.FETCHING) {
        state.candidateState[state.inFlight.id] = ST.UNSEEN;
        if (state.hops > 0) state.hops -= 1;
      }
    }
    state.inFlight = null;
  });

  // BFCache から復帰した場合は再開できるようにする
  window.addEventListener('pageshow', function (ev) {
    if (ev && ev.persisted) {
      teardownRequested = false;
      log('BFCache から復帰');
      // 中断した処理は残っていないので、現在のスクロール位置から判定をやり直す
      if (state.latched && !state.revoked) onScrollCheck();
    }
  });

  if (DEBUG) {
    // 手動で挙動を確認するための入口（DEBUG 時のみ公開）
    window.__infinite = {
      state: state,
      cfg: CFG,
      historyEnabled: function () { return historyEnabled; },
      sentinel: function () { return sentinel; },
      // latch を待たずに監視を開始する
      forceLatch: function () {
        if (!state.latched) { state.latched = true; removeLatchListeners(); startObservers(); }
        return 'latched=' + state.latched + ' sentinelInDom=' + !!sentinel.parentNode;
      },
      // sentinel の交差を待たずに1件読み込む
      loadNow: function () { maybeLoadNext(); return 'requested. inFlight=' + !!state.inFlight; },
      // 表示中記事の切り替えを手で起こす。
      // 非表示タブでは requestAnimationFrame も IntersectionObserver も止まるため、
      // 主可視判定（URL / title / コメント投稿先）を実スクロールでは確認できない。
      // SP/PC の判定をその場でやり直す（resize イベントが飛ばない環境での確認用）
      followSidebar: function (show) {
        if (!followSidebar) return 'サイドバー追従は無効';
        followSidebar(show !== false);
        var p = document.querySelector('.infinite-sidebar-follow');
        if (!p) return '追従パネルが作られませんでした';
        var r = p.getBoundingClientRect();
        return { display: p.style.display, x: Math.round(r.left), y: Math.round(r.top),
                 w: Math.round(r.width), h: Math.round(r.height),
                 nodes: p.getElementsByTagName('*').length,
                 imgs: p.querySelectorAll('img').length };
      },
      syncSidebar: function () {
        if (!syncSidebar) return 'サイドバー移動は無効';
        syncSidebar();
        var bar = document.querySelector(boot.sidebar_selector);
        return bar ? bar.parentNode.className : 'サイドバーなし';
      },
      // 現在のスクロール位置で主可視判定をやり直す（rAF/IO が止まる環境での確認用）
      recheck: function () {
        checkPrimaryByRect();
        return { url: window.location.pathname, id: state.current && state.current.id };
      },
      setPrimary: function (i) {
        var a = state.articles[i];
        if (!a) return 'index 範囲外 (0..' + (state.articles.length - 1) + ')';
        commitPrimary(a);
        return { url: window.location.pathname, title: document.title,
                 commentAction: movedCommentForm ? movedCommentForm.getAttribute('action') : null };
      },
      // 現在の判断材料をまとめて出す
      why: function () {
        return {
          latched: state.latched, revoked: state.revoked, inFlight: !!state.inFlight,
          appended: state.appended, maxAppends: state.maxAppends, hops: state.hops,
          triggerProfile: boot.trigger_profile, triggerMargin: CFG.triggerMargin,
          minScrollSinceAppend: CFG.minScrollSinceAppend, consumeRatio: CFG.consumeRatio,
          next: state.next, chain: state.chain, chainIdx: state.chainIdx,
          listSources: listSources, listSourceIdx: listSourceIdx, listFetchState: listFetchState,
          listPagesSeen: listPagesSeen.slice(), listPendingPage: listPendingPage,
          deepPageAttempted: deepPageAttempted,
          listNetworkFetches: listNetworkFetches, maxListFetches: CFG.maxListFetches,
          listCandidates: listCandidates.map(function (x) {
            return { id: x.article_id, rank: x.rank, weight: x.weight, title: x.title };
          }),
          selectedRanks: selectedRanks.slice(), lastListDebug: lastListDebug,
          finalizedSourceIdx: finalizedSourceIdx,
          handleBlocklist: handleBlocklist.slice(), recencyExponent: CFG.recencyExponent,
          infiniteTag: boot.infinite_tag, articleTags: boot.article_tags, nextMode: boot.next_mode,
          measurementMode: state.measurementMode, historyEnabled: historyEnabled,
          sentinelInDom: !!sentinel.parentNode,
          readyForNextAppend: readyForNextAppend(),
          lastAppendedIsConsumed: lastAppendedIsConsumed(),
          scrolledEnoughSinceAppend: scrolledEnoughSinceAppend(),
          sentinelRect: sentinel.parentNode ? JSON.stringify(sentinel.getBoundingClientRect()) : null,
          viewportH: window.innerHeight,
          containerFound: !!container, initialElFound: !!initialEl,
          ioSupported: ('IntersectionObserver' in window)
        };
      }
    };
  }

  log('initialized', {
    generation: state.generation,
    next: state.next && state.next.article_id,
    historyEnabled: historyEnabled,
    measurementMode: state.measurementMode,
    maxAppends: state.maxAppends
  });
})();
